import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type ReactNode, useMemo, useState, useEffect, useRef } from "react";
import { useSupportedLanguages } from "@/hooks/useSupportedLanguages";
import {
  Button,
  Chip,
  Icon,
  Input,
  Modal,
  SearchBar,
  Select,
  Toast,
} from "@legal-500/legal500-design-system";
import type { ToastType } from "@legal-500/legal500-design-system";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchAllLibraryQuestions,
  type LibraryQuestion,
  type DisplayFilterRule,
  type CreateQuestionBody,
  type UpdateQuestionBody,
  getLocalizedTextDefault,
} from "@/api/survey-library.service";
import { surveysService, type UpdateSurveyBody } from "@/api/surveys.service";
import {
  EMPTY_DISPLAY_RULE_OPTIONS,
  loadLegacyDisplayRuleOptions,
  loadNewAxisDisplayRuleOptions,
  newAxisEditionLabel,
  newAxisEditionOptions,
  type DisplayRuleOptions,
} from "@/api/surveyTaxonomy.service";
import { ErrorBanner } from "@/components/ErrorBanner/ErrorBanner";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import EmptyState from "@/components/EmptyState";
import LoadingState from "@/components/LoadingState";
import { PageHeader } from "@/components/PageHeader";
import { useMutation, useQuery } from "@/hooks/useApi";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useNewAxisEditions } from "@/hooks/useNewAxisEditions";
import { usePublications } from "@/hooks/usePublications";
import { NEW_TAXONOMY_FLAG } from "@/hooks/useTaxonomyMode";
import type { LegacyPublication } from "@/api/legacy-publications.service";
import { ROUTES } from "@/routes";
import {
  FilterRulesEditor,
  type FilterRulesEditorHandle,
} from "../QuestionLibrary/FilterRulesEditor";
import { QuestionFormModal } from "../QuestionLibrary/QuestionFormModal";
import {
  DuplicateSurveyWarning,
  ExceptionConditionsFields,
  hasExceptionConditions,
  surveyConditionsToPayloadFields,
  toSurveyConditionValues,
} from "../surveyConditions";
import { useDuplicateSurveyWarning } from "../useDuplicateSurveyWarning";
import {
  AUTO_TRANSLATE_BATCH_MAX_CHARS,
  AUTO_TRANSLATE_BATCH_SIZE,
  AUTO_TRANSLATE_CONCURRENCY,
  DEFAULT_HEADER_TAG,
  DUPLICATE_SURVEY_CONFLICT_MESSAGE,
  EDITION_LOCKED_BY_TAXONOMY_RULES_MESSAGE,
  HEADER_TAG_OPTIONS,
  isSurveyEditionPublicationTakenConflict,
  PUBLICATION_LOCKED_BY_TAXONOMY_RULES_MESSAGE,
  STATUS_CHIP_COLOR,
  type RuleValueLabelMaps,
} from "./surveyDetails.constants";
import type {
  ApiError,
  BackendMissingTranslation,
  ContentFormState,
  DetailsTab,
  EditableSurveyState,
  FlattenedSurveyItem,
  SurveyItemKind,
  TranslationSelection,
  TranslationStatus,
} from "./surveyDetails.types";
import { MissingTranslationsModal } from "./MissingTranslationsModal";
import { SurveyPreviewTab, EMPTY_CONTEXT, type PreviewContext } from "./SurveyPreviewTab";
import {
  areNestedNumberKeyedRecordsEqual,
  areNestedStringKeyedRecordsEqual,
  appendElementToFirstPage,
  areStringRecordsEqual,
  buildContentElement,
  buildDividerElement,
  countTestimonialQuestions,
  definitionHasPublicationScopedTaxonomy,
  extractFooterTranslationsFromRawDefinition,
  extractHeaderTranslationsFromRawDefinition,
  extractSourceTitle,
  extractTranslationsFromRawDefinition,
  flattenSurveyItems,
  formatDisplayRule,
  getChoiceSourceText,
  getChoiceTranslationKey,
  getChoiceValue,
  getDescriptionSourceText,
  getDescriptionTranslationKey,
  getLabelFieldSourceText,
  getLabelFieldTranslationKey,
  getTranslatableLabelFields,
  LOCALIZED_LABEL_FIELDS,
  type LocalizedLabelFieldKey,
  getFooterSourceText,
  getFooterText,
  getHeaderSourceText,
  getHeaderText,
  getItemKindLabel,
  getItemSourceText,
  getLanguageFlag,
  getSurveyQuestionTypeLabel,
  getTranslationItemKey,
  getRangeLabelSourceText,
  getRangeLabelTranslationKey,
  hasTranslatableChoices,
  hasTranslatableQuestionDescription,
  hasTranslatableRangeLabels,
  htmlToPlainText,
  isFooterFullyTranslated,
  isHeaderFullyTranslated,
  isHeaderTag,
  getItemTranslationStatus,
  getSectionTranslationStatus,
  FOOTER_SECTION,
  HEADER_SECTION,
  mapLibraryQuestionToSurveyElement,
  mapQuestionPayloadToSurveyElement,
  mergeEditedQuestionElement,
  mapSurveyElementToLibraryQuestion,
  mergeFooterTranslationsIntoDefinition,
  mergeHeaderTranslationsIntoDefinition,
  mergeTranslationsIntoDefinition,
  moveElementToIndex,
  parseHeaderTagFromHtml,
  prepareDefinitionForSave,
  removeElementAtOrder,
  setFooterText,
  setHeaderText,
  toEditableSurveyState,
  toSurveyEditionFields,
  toSurveyDefinition,
  updateElementAtOrder,
} from "./surveyDetails.utils";
import { filterLibraryQuestions } from "./libraryQuestionSearch";
import styles from "./SurveyDetails.module.css";

type TranslationEntryMap = Record<string, { value: string; sourceSnapshot: string }>;

/** When `supportedLanguages` updates after fetch, keep in-flight / edited per-key values on top of freshly extracted rows. */
function mergeItemTranslationsFromExtract(
  prev: Record<string, TranslationEntryMap>,
  extracted: Record<string, TranslationEntryMap>,
): Record<string, TranslationEntryMap> {
  const langs = new Set([...Object.keys(extracted), ...Object.keys(prev)]);
  const merged: Record<string, TranslationEntryMap> = {};
  for (const lang of langs) {
    merged[lang] = { ...(extracted[lang] ?? {}), ...(prev[lang] ?? {}) };
  }
  return merged;
}

function mergeSectionTranslationsFromExtract(
  prev: TranslationEntryMap,
  extracted: TranslationEntryMap,
): TranslationEntryMap {
  return { ...extracted, ...prev };
}

function renderDisplayRulesCompact(
  displayRules: DisplayFilterRule[],
  valueLabelMaps?: RuleValueLabelMaps,
) {
  if (displayRules.length === 0) {
    return <span className={styles.mutedText}>All respondents</span>;
  }

  return (
    <div className={styles.rulesCell}>
      {displayRules.map((rule, ruleIndex) => {
        const entries = formatDisplayRule(rule, valueLabelMaps);
        if (entries.length === 0) return null;

        return (
          <div
            key={`${ruleIndex}-${entries.map(entry => entry.value).join("-")}`}
            className={styles.rulePill}
          >
            {entries.map((entry, idx) => (
              <span key={`${entry.label}-${entry.value}`} className={styles.ruleTag}>
                {idx > 0 && <span className={styles.pillSeparator}>And</span>}
                <span className={styles.ruleTagLabel}>{entry.label}:</span>
                <span className={styles.ruleTagValue}>{entry.value}</span>
              </span>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function renderLibraryRules(
  displayRules: DisplayFilterRule[] | null | undefined,
  valueLabelMaps?: RuleValueLabelMaps,
) {
  if (!Array.isArray(displayRules) || displayRules.length === 0) {
    return <span className={styles.mutedText}>Rules: All respondents</span>;
  }

  const ruleEntries = displayRules
    .map(rule => formatDisplayRule(rule, valueLabelMaps))
    .filter(entries => entries.length > 0);

  if (ruleEntries.length === 0) {
    return <span className={styles.mutedText}>Rules: All respondents</span>;
  }

  return (
    <div className={styles.libraryRulesBlock}>
      <span className={styles.libraryRulesHeading}>Rules:</span>
      <div className={styles.libraryRulesTable}>
        {ruleEntries.map((entries, index) => (
          <div
            key={`rule-row-${index}-${entries.map(entry => `${entry.label}:${entry.value}`).join("|")}`}
            className={styles.libraryRuleRow}
          >
            {entries.map((entry, idx) => (
              <span key={`${entry.label}-${entry.value}`} className={styles.libraryRuleCell}>
                {idx > 0 && <span className={styles.pillSeparator}>And</span>}
                <span className={styles.libraryRuleCellLabel}>{entry.label}:</span> {entry.value}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

type SortableQuestionCardProps = {
  id: string;
  children: ReactNode;
};

const stopDragPropagation = (event: React.PointerEvent | React.MouseEvent | React.TouchEvent) => {
  event.stopPropagation();
};

function SortableQuestionCard({ id, children }: SortableQuestionCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={styles.questionCard}
      {...attributes}
      {...listeners}
      aria-label="Drag to reorder question"
    >
      <span aria-hidden="true" className={styles.dragHandle}>
        <span className={styles.dragDots}>⋮⋮</span>
      </span>
      {children}
    </div>
  );
}

function SurveyDetailsPage() {
  const navigate = useNavigate();
  const { surveyId } = useParams<{ surveyId: string }>();
  const [activeTab, setActiveTab] = useState<DetailsTab>("questions");
  const [draftSurvey, setDraftSurvey] = useState<EditableSurveyState | null>(null);
  // Bare year ("2026") used as the publications filter + Edition dropdown value.
  // The full `draftSurvey.edition` string ("2026 Edition ...") is only set once a
  // publication is chosen — mirroring the create modal (SurveyFormModal).
  const [selectedEditionYear, setSelectedEditionYear] = useState("");
  const [questionModal, setQuestionModal] = useState<{
    open: boolean;
    mode: "add" | "edit";
    order: number | null;
  }>({
    open: false,
    mode: "add",
    order: null,
  });
  const [editingQuestion, setEditingQuestion] = useState<LibraryQuestion | null>(null);
  const [contentModal, setContentModal] = useState<{
    open: boolean;
    mode: "add" | "edit";
    kind: Extract<SurveyItemKind, "header" | "freeText">;
    order: number | null;
  }>({
    open: false,
    mode: "add",
    kind: "header",
    order: null,
  });
  const [contentForm, setContentForm] = useState<ContentFormState>({
    text: "",
    headerTag: DEFAULT_HEADER_TAG,
  });
  const [contentDisplayRules, setContentDisplayRules] = useState<DisplayFilterRule[]>([]);
  const contentDisplayRulesEditorRef = useRef<FilterRulesEditorHandle>(null);
  const [libraryModalOpen, setLibraryModalOpen] = useState(false);
  const [librarySearch, setLibrarySearch] = useState("");
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [testimonialWarningOpen, setTestimonialWarningOpen] = useState(false);
  const [serverMissingTranslations, setServerMissingTranslations] = useState<
    BackendMissingTranslation[] | null
  >(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [settingsErrors, setSettingsErrors] = useState<Record<string, string>>({});
  const [conditionsExpanded, setConditionsExpanded] = useState(false);
  const [publishValidationError, setPublishValidationError] = useState<string | null>(null);
  const [selectedTranslation, setSelectedTranslation] = useState<TranslationSelection>(null);
  const [translationsByLanguage, setTranslationsByLanguage] = useState<
    Record<string, Record<string, { value: string; sourceSnapshot: string }>>
  >({});
  const [footerTranslationsByLanguage, setFooterTranslationsByLanguage] = useState<
    Record<string, { value: string; sourceSnapshot: string }>
  >({});
  const [footerTranslationEditorValues, setFooterTranslationEditorValues] = useState<
    Record<string, string>
  >({});
  const [headerTranslationsByLanguage, setHeaderTranslationsByLanguage] = useState<
    Record<string, { value: string; sourceSnapshot: string }>
  >({});
  const [headerTranslationEditorValues, setHeaderTranslationEditorValues] = useState<
    Record<string, string>
  >({});
  const [translationEditorValues, setTranslationEditorValues] = useState<Record<string, string>>(
    {},
  );
  const [choiceTranslationEditorValues, setChoiceTranslationEditorValues] = useState<
    Record<string, Record<number, string>>
  >({});
  const [rangeLabelTranslationEditorValues, setRangeLabelTranslationEditorValues] = useState<
    Record<string, Record<string, string>>
  >({});
  const [descriptionTranslationEditorValues, setDescriptionTranslationEditorValues] = useState<
    Record<string, string>
  >({});
  const [labelFieldTranslationEditorValues, setLabelFieldTranslationEditorValues] = useState<
    Partial<Record<LocalizedLabelFieldKey, Record<string, string>>>
  >({});
  const { allLanguages: supportedLanguageOptions, translatableLanguages: supportedLanguages } =
    useSupportedLanguages();
  const [autoTranslateLoading, setAutoTranslateLoading] = useState(false);
  const [taxonomyRuleOptions, setTaxonomyRuleOptions] = useState<DisplayRuleOptions>(
    EMPTY_DISPLAY_RULE_OPTIONS,
  );
  const [previewContext, setPreviewContext] = useState<PreviewContext>(EMPTY_CONTEXT);
  const [previewCollapsed, setPreviewCollapsed] = useState(false);

  useEffect(() => {
    setPreviewContext(EMPTY_CONTEXT);
    setPreviewCollapsed(false);
  }, [surveyId]);

  const {
    data: surveyResponse,
    loading,
    error,
    refetch,
    isRefetching,
  } = useQuery(() => {
    if (!surveyId) {
      throw new Error("Survey id is required.");
    }
    return surveysService.getById(surveyId);
  }, [surveyId]);

  const {
    data: libraryResponse,
    loading: libraryLoading,
    error: libraryError,
    refetch: refetchLibrary,
  } = useQuery(() => fetchAllLibraryQuestions(), []);

  // Pure UI filter over the already-loaded in-memory library; never refetches.
  const filteredLibraryQuestions = useMemo(
    () => filterLibraryQuestions(libraryResponse?.questions ?? [], librarySearch),
    [libraryResponse, librarySearch],
  );
  const newAxisSurvey = Boolean(draftSurvey?.editionId);
  const { hasFlag } = useFeatureFlags();
  const newTaxonomyEnabled = hasFlag(NEW_TAXONOMY_FLAG);
  const newAxisEditable = newAxisSurvey && newTaxonomyEnabled;
  const { data: publicationsData } = usePublications(
    { edition: selectedEditionYear || undefined },
    { enabled: !newAxisSurvey && Boolean(selectedEditionYear) },
  );
  const { data: newAxisEditions, error: newAxisEditionsError } = useNewAxisEditions({
    enabled: newAxisEditable,
  });

  const { mutate: updateSurvey, loading: saveLoading } = useMutation(
    (args: { id: string; body: UpdateSurveyBody }) => surveysService.update(args.id, args.body),
  );
  const { mutate: publishSurvey, loading: publishLoading } = useMutation((id: string) =>
    surveysService.publish(id),
  );

  const survey = surveyResponse?.survey;
  const flattenedItems = useMemo(
    () => flattenSurveyItems(draftSurvey?.definition ?? { pages: [] }),
    [draftSurvey?.definition],
  );
  // Dividers are purely visual (an <hr />) and carry no translatable text, so
  // they are excluded from every translation-facing view and counter.
  const translatableItems = useMemo(
    () => flattenedItems.filter(item => item.kind !== "divider"),
    [flattenedItems],
  );
  const footerSourceText = useMemo(
    () => getFooterSourceText(draftSurvey?.definition ?? { pages: [] }),
    [draftSurvey?.definition],
  );
  const hasFooterToTranslate = footerSourceText.trim().length > 0;
  const headerSourceText = useMemo(
    () => getHeaderSourceText(draftSurvey?.definition ?? { pages: [] }),
    [draftSurvey?.definition],
  );
  const hasHeaderToTranslate = headerSourceText.trim().length > 0;
  const isNotFound = Boolean((error as (Error & { status?: number }) | null)?.status === 404);
  const dragSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const subtitle = useMemo(() => {
    if (!survey) {
      return "Survey details";
    }
    return `${survey.edition} | ${survey.publication}`;
  }, [survey]);
  const selectedPublicationIdNumber =
    draftSurvey?.publicationId && Number.isFinite(draftSurvey.publicationId)
      ? Number(draftSurvey.publicationId)
      : null;

  // In Details, collapsing the section is purely visual — it never mutates the
  // survey's conditions (unlike the create modal, where collapse = discard).
  // Reverting to general is explicit: uncheck every box. Save payload and the
  // duplicate warning share this expression so they cannot drift.
  const effectiveConditions = toSurveyConditionValues(draftSurvey);

  const duplicateSurvey = useDuplicateSurveyWarning({
    enabled: Boolean(draftSurvey),
    edition: draftSurvey?.edition || undefined,
    publicationId: selectedPublicationIdNumber ?? undefined,
    editionId: draftSurvey?.editionId ?? undefined,
    newTaxonomyEnabled,
    conditions: effectiveConditions,
    // Never warn about the survey being edited itself.
    excludeSurveyId: surveyId,
  });
  const ruleValueLabelMaps = useMemo(
    () => ({
      region: new Map(taxonomyRuleOptions.region.map(option => [option.value, option.label])),
      practiceGroup: new Map(
        taxonomyRuleOptions.practiceGroup.map(option => [option.value, option.label]),
      ),
      practiceArea: new Map(
        taxonomyRuleOptions.practiceArea.map(option => [option.value, option.label]),
      ),
    }),
    [taxonomyRuleOptions],
  );

  const initializedForSurveyIdRef = useRef<string | null>(null);

  useEffect(() => {
    initializedForSurveyIdRef.current = null;
    setDraftSurvey(null);
    setSelectedEditionYear("");
  }, [surveyId]);

  useEffect(() => {
    if (!survey || !surveyId || survey.id !== surveyId) {
      return;
    }
    if (initializedForSurveyIdRef.current === surveyId) {
      return;
    }
    initializedForSurveyIdRef.current = surveyId;
    setDraftSurvey(toEditableSurveyState(survey));
    setConditionsExpanded(hasExceptionConditions(survey));
    // Derive the bare year from the stored full edition string ("2026 Edition ...").
    setSelectedEditionYear(survey.edition?.match(/\d{4}/)?.[0] ?? "");
    const items = flattenSurveyItems(toSurveyDefinition(survey.definition as object));
    const extracted = extractTranslationsFromRawDefinition(
      survey.definition as object,
      items,
      supportedLanguages,
    );
    setTranslationsByLanguage(extracted);
    const footerExtracted = extractFooterTranslationsFromRawDefinition(
      survey.definition as object,
      supportedLanguages,
    );
    setFooterTranslationsByLanguage(footerExtracted);
    const headerExtracted = extractHeaderTranslationsFromRawDefinition(
      survey.definition as object,
      supportedLanguages,
    );
    setHeaderTranslationsByLanguage(headerExtracted);
  }, [survey, surveyId, supportedLanguages]);

  useEffect(() => {
    if (!survey || !surveyId || survey.id !== surveyId) {
      return;
    }
    const items = flattenSurveyItems(toSurveyDefinition(survey.definition as object));
    const extracted = extractTranslationsFromRawDefinition(
      survey.definition as object,
      items,
      supportedLanguages,
    );
    const footerExtracted = extractFooterTranslationsFromRawDefinition(
      survey.definition as object,
      supportedLanguages,
    );
    const headerExtracted = extractHeaderTranslationsFromRawDefinition(
      survey.definition as object,
      supportedLanguages,
    );
    setTranslationsByLanguage(prev => mergeItemTranslationsFromExtract(prev, extracted));
    setFooterTranslationsByLanguage(prev =>
      mergeSectionTranslationsFromExtract(prev, footerExtracted),
    );
    setHeaderTranslationsByLanguage(prev =>
      mergeSectionTranslationsFromExtract(prev, headerExtracted),
    );
  }, [survey, surveyId, supportedLanguages]);

  const ruleOptionsEditionId = draftSurvey?.editionId ?? null;

  useEffect(() => {
    const request = ruleOptionsEditionId
      ? loadNewAxisDisplayRuleOptions(ruleOptionsEditionId)
      : selectedPublicationIdNumber
        ? loadLegacyDisplayRuleOptions(selectedPublicationIdNumber)
        : null;
    if (!request) {
      setTaxonomyRuleOptions(EMPTY_DISPLAY_RULE_OPTIONS);
      return;
    }

    let cancelled = false;
    request
      .then(options => {
        if (!cancelled) setTaxonomyRuleOptions(options);
      })
      .catch(error => {
        if (cancelled) return;
        console.error("Failed to load display rule options", {
          error,
          editionId: ruleOptionsEditionId,
          publicationId: selectedPublicationIdNumber,
        });
        setTaxonomyRuleOptions(EMPTY_DISPLAY_RULE_OPTIONS);
        setToast({ message: "Failed to load display rule options.", type: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [ruleOptionsEditionId, selectedPublicationIdNumber]);

  const translatableLanguages = supportedLanguages;

  const previewDefinition = useMemo(() => {
    if (!draftSurvey) return { pages: [] };
    let merged = mergeTranslationsIntoDefinition(
      draftSurvey.definition,
      translationsByLanguage,
      flattenedItems,
      translatableLanguages,
    );
    merged = mergeFooterTranslationsIntoDefinition(merged, footerTranslationsByLanguage);
    merged = mergeHeaderTranslationsIntoDefinition(merged, headerTranslationsByLanguage);
    return merged;
  }, [
    draftSurvey,
    translationsByLanguage,
    footerTranslationsByLanguage,
    headerTranslationsByLanguage,
    flattenedItems,
    translatableLanguages,
  ]);

  const publicationOptions = useMemo(() => {
    const pubs = publicationsData?.publications ?? [];
    return [
      { value: "", label: "Select Publication" },
      ...pubs.map((p: LegacyPublication) => ({
        value: String(p.id),
        label: p.publication ? `${p.publication} (${p.edition ?? "n/a"})` : `Publication ${p.id}`,
      })),
    ];
  }, [publicationsData?.publications]);
  const selectedPublicationId = draftSurvey?.publicationId ? String(draftSurvey.publicationId) : "";
  const storedNewAxisEditionLabel = draftSurvey
    ? newAxisEditionLabel(draftSurvey.publication, draftSurvey.edition)
    : "";
  const newAxisEditionSelectOptions = useMemo(() => {
    const options = newAxisEditionOptions(newAxisEditions);
    const currentEditionId = draftSurvey?.editionId;
    if (!currentEditionId || options.some(option => option.value === currentEditionId)) {
      return options;
    }
    return [{ value: currentEditionId, label: storedNewAxisEditionLabel }, ...options];
  }, [newAxisEditions, draftSurvey?.editionId, storedNewAxisEditionLabel]);

  const selectedTranslationItem = useMemo(
    () =>
      selectedTranslation?.kind === "item"
        ? (flattenedItems.find(item => getTranslationItemKey(item) === selectedTranslation.key) ??
          null)
        : null,
    [flattenedItems, selectedTranslation],
  );

  useEffect(() => {
    if (translatableItems.length === 0) {
      setSelectedTranslation(prev => (prev?.kind === "item" ? null : prev));
      return;
    }

    setSelectedTranslation(prev => {
      if (prev?.kind === "footer" || prev?.kind === "header") return prev;
      if (!prev) {
        return { kind: "item", key: getTranslationItemKey(translatableItems[0]) };
      }
      const stillExists = translatableItems.some(item => getTranslationItemKey(item) === prev.key);
      return stillExists
        ? prev
        : { kind: "item", key: getTranslationItemKey(translatableItems[0]) };
    });
  }, [translatableItems]);

  const writeTranslationEdit = (
    language: string,
    translationKey: string,
    value: string,
    sourceSnapshot: string,
  ) => {
    setTranslationsByLanguage(prev => {
      const languageEntries = { ...(prev[language] ?? {}) };
      if (value.trim()) {
        languageEntries[translationKey] = { value, sourceSnapshot };
      } else {
        delete languageEntries[translationKey];
      }
      return { ...prev, [language]: languageEntries };
    });
  };

  const getTranslationStatus = (item: FlattenedSurveyItem, language: string): TranslationStatus => {
    const languageTranslations = translationsByLanguage[language];
    const translationKey = getTranslationItemKey(item);
    const sourceText = getItemSourceText(item);
    const translationEntry = languageTranslations?.[translationKey];

    if (!translationEntry || !translationEntry.value.trim()) {
      return "missing";
    }

    if (translationEntry.sourceSnapshot !== sourceText) {
      return "outdated";
    }

    return "translated";
  };

  useEffect(() => {
    if (!selectedTranslationItem) {
      setTranslationEditorValues({});
      setChoiceTranslationEditorValues({});
      setRangeLabelTranslationEditorValues({});
      setDescriptionTranslationEditorValues({});
      setLabelFieldTranslationEditorValues({});
      return;
    }

    const translationKey = getTranslationItemKey(selectedTranslationItem);
    const nextValues = translatableLanguages.reduce(
      (acc, language) => {
        acc[language] = translationsByLanguage[language]?.[translationKey]?.value ?? "";
        return acc;
      },
      {} as Record<string, string>,
    );
    setTranslationEditorValues(prev =>
      areStringRecordsEqual(prev, nextValues, translatableLanguages) ? prev : nextValues,
    );

    if (hasTranslatableChoices(selectedTranslationItem.element)) {
      const choices = selectedTranslationItem.element.choices!;
      const nextChoiceValues = translatableLanguages.reduce(
        (acc, language) => {
          acc[language] = choices.reduce(
            (cacc, _, choiceIdx) => {
              const choiceKey = getChoiceTranslationKey(selectedTranslationItem, choiceIdx);
              cacc[choiceIdx] = translationsByLanguage[language]?.[choiceKey]?.value ?? "";
              return cacc;
            },
            {} as Record<number, string>,
          );
          return acc;
        },
        {} as Record<string, Record<number, string>>,
      );
      setChoiceTranslationEditorValues(prev =>
        areNestedNumberKeyedRecordsEqual(prev, nextChoiceValues, translatableLanguages)
          ? prev
          : nextChoiceValues,
      );
    } else {
      setChoiceTranslationEditorValues({});
    }

    if (hasTranslatableRangeLabels(selectedTranslationItem.element)) {
      const labels = [...selectedTranslationItem.element.rangeLabels!].sort(
        (a, b) => a.order - b.order,
      );
      const nextRangeLabelValues = translatableLanguages.reduce(
        (acc, language) => {
          acc[language] = labels.reduce(
            (racc, rl) => {
              const rlKey = getRangeLabelTranslationKey(selectedTranslationItem, rl.id);
              racc[rl.id] = translationsByLanguage[language]?.[rlKey]?.value ?? "";
              return racc;
            },
            {} as Record<string, string>,
          );
          return acc;
        },
        {} as Record<string, Record<string, string>>,
      );
      setRangeLabelTranslationEditorValues(prev =>
        areNestedStringKeyedRecordsEqual(prev, nextRangeLabelValues, translatableLanguages)
          ? prev
          : nextRangeLabelValues,
      );
    } else {
      setRangeLabelTranslationEditorValues({});
    }

    if (hasTranslatableQuestionDescription(selectedTranslationItem)) {
      const descKey = getDescriptionTranslationKey(selectedTranslationItem);
      const nextDescValues = translatableLanguages.reduce(
        (acc, language) => {
          acc[language] = translationsByLanguage[language]?.[descKey]?.value ?? "";
          return acc;
        },
        {} as Record<string, string>,
      );
      setDescriptionTranslationEditorValues(prev =>
        areStringRecordsEqual(prev, nextDescValues, translatableLanguages) ? prev : nextDescValues,
      );
    } else {
      setDescriptionTranslationEditorValues({});
    }

    const nextLabelFieldValues: Partial<Record<LocalizedLabelFieldKey, Record<string, string>>> =
      {};
    for (const field of getTranslatableLabelFields(selectedTranslationItem)) {
      const labelKey = getLabelFieldTranslationKey(selectedTranslationItem, field);
      nextLabelFieldValues[field.key] = translatableLanguages.reduce(
        (acc, language) => {
          acc[language] = translationsByLanguage[language]?.[labelKey]?.value ?? "";
          return acc;
        },
        {} as Record<string, string>,
      );
    }
    setLabelFieldTranslationEditorValues(prev =>
      LOCALIZED_LABEL_FIELDS.every(
        field =>
          field.key in prev === field.key in nextLabelFieldValues &&
          areStringRecordsEqual(
            prev[field.key] ?? {},
            nextLabelFieldValues[field.key] ?? {},
            translatableLanguages,
          ),
      )
        ? prev
        : nextLabelFieldValues,
    );
  }, [selectedTranslationItem, translatableLanguages, translationsByLanguage]);

  useEffect(() => {
    if (selectedTranslation?.kind !== "footer") return;
    const next: Record<string, string> = {};
    for (const lang of translatableLanguages) {
      next[lang] = footerTranslationsByLanguage[lang]?.value ?? "";
    }
    setFooterTranslationEditorValues(prev =>
      areStringRecordsEqual(prev, next, translatableLanguages) ? prev : next,
    );
  }, [selectedTranslation, footerTranslationsByLanguage, translatableLanguages]);

  useEffect(() => {
    if (selectedTranslation?.kind !== "header") return;
    const next: Record<string, string> = {};
    for (const lang of translatableLanguages) {
      next[lang] = headerTranslationsByLanguage[lang]?.value ?? "";
    }
    setHeaderTranslationEditorValues(prev =>
      areStringRecordsEqual(prev, next, translatableLanguages) ? prev : next,
    );
  }, [selectedTranslation, headerTranslationsByLanguage, translatableLanguages]);

  const isItemFullyTranslated = (item: FlattenedSurveyItem): boolean => {
    const titleOk = translatableLanguages.every(
      language => getTranslationStatus(item, language) === "translated",
    );
    if (!titleOk) return false;

    if (hasTranslatableQuestionDescription(item)) {
      const descOk = translatableLanguages.every(language => {
        const descKey = getDescriptionTranslationKey(item);
        const entry = translationsByLanguage[language]?.[descKey];
        return (
          entry?.value?.trim() && entry.sourceSnapshot === getDescriptionSourceText(item.element)
        );
      });
      if (!descOk) return false;
    }

    for (const field of getTranslatableLabelFields(item)) {
      const labelKey = getLabelFieldTranslationKey(item, field);
      const labelSource = getLabelFieldSourceText(item.element, field);
      const labelOk = translatableLanguages.every(language => {
        const entry = translationsByLanguage[language]?.[labelKey];
        return entry?.value?.trim() && entry.sourceSnapshot === labelSource;
      });
      if (!labelOk) return false;
    }

    if (hasTranslatableRangeLabels(item.element)) {
      const labels = [...item.element.rangeLabels!].sort((a, b) => a.order - b.order);
      const rangeOk = translatableLanguages.every(language =>
        labels.every(rl => {
          const rlKey = getRangeLabelTranslationKey(item, rl.id);
          const entry = translationsByLanguage[language]?.[rlKey];
          return entry?.value?.trim() && entry.sourceSnapshot === getRangeLabelSourceText(rl);
        }),
      );
      if (!rangeOk) return false;
    }

    if (!hasTranslatableChoices(item.element)) return true;

    const choices = item.element.choices!;
    return translatableLanguages.every(language =>
      choices.every((choice, idx) => {
        const choiceKey = getChoiceTranslationKey(item, idx);
        const entry = translationsByLanguage[language]?.[choiceKey];
        return entry?.value?.trim() && entry.sourceSnapshot === getChoiceSourceText(choice);
      }),
    );
  };

  const translationProgress = useMemo(() => {
    const itemsTranslated = translatableItems.filter(item => isItemFullyTranslated(item)).length;
    const itemsTotal = translatableItems.length;
    const headerCounts = hasHeaderToTranslate ? 1 : 0;
    const headerTranslated =
      hasHeaderToTranslate &&
      isHeaderFullyTranslated(
        draftSurvey?.definition ?? { pages: [] },
        headerTranslationsByLanguage,
        translatableLanguages,
      )
        ? 1
        : 0;
    const footerCounts = hasFooterToTranslate ? 1 : 0;
    const footerTranslated =
      hasFooterToTranslate &&
      isFooterFullyTranslated(
        draftSurvey?.definition ?? { pages: [] },
        footerTranslationsByLanguage,
        translatableLanguages,
      )
        ? 1
        : 0;
    return {
      translated: itemsTranslated + headerTranslated + footerTranslated,
      total: itemsTotal + headerCounts + footerCounts,
    };
  }, [
    translatableItems,
    translationsByLanguage,
    translatableLanguages,
    hasHeaderToTranslate,
    headerTranslationsByLanguage,
    hasFooterToTranslate,
    footerTranslationsByLanguage,
    draftSurvey?.definition,
  ]);
  const missingTranslationCount = useMemo(() => {
    let missing = translatableItems.filter(item => !isItemFullyTranslated(item)).length;
    const def = draftSurvey?.definition ?? { pages: [] };
    if (
      hasHeaderToTranslate &&
      !isHeaderFullyTranslated(def, headerTranslationsByLanguage, translatableLanguages)
    ) {
      missing += 1;
    }
    if (
      hasFooterToTranslate &&
      !isFooterFullyTranslated(def, footerTranslationsByLanguage, translatableLanguages)
    ) {
      missing += 1;
    }
    return missing;
  }, [
    translatableItems,
    translationsByLanguage,
    translatableLanguages,
    hasHeaderToTranslate,
    headerTranslationsByLanguage,
    hasFooterToTranslate,
    footerTranslationsByLanguage,
    draftSurvey?.definition,
  ]);
  const hasMissingTranslations = missingTranslationCount > 0;
  const publishBlockedReason = "Complete all required translations before publishing.";
  const publishBlockedMessage =
    missingTranslationCount > 0
      ? `${publishBlockedReason} Missing: ${missingTranslationCount} item(s).`
      : publishBlockedReason;
  const isPublishedSurvey = draftSurvey?.status === "published";
  const taxonomyLocksEditionPublication = useMemo(
    () =>
      Boolean(
        draftSurvey &&
          draftSurvey.status !== "published" &&
          definitionHasPublicationScopedTaxonomy(draftSurvey.definition),
      ),
    [draftSurvey],
  );
  useEffect(() => {
    if (!publishValidationError) {
      return;
    }

    if (hasMissingTranslations) {
      return;
    }

    setPublishValidationError(null);
  }, [hasMissingTranslations, publishValidationError]);

  /* Only replace the whole page when we have no survey payload yet. useQuery sets loading on
   * every refetch but isRefetching is only true for explicit refetch() — otherwise filters
   * / saves would flash this screen and feel like content is lost. */
  const surveyMatchesRoute = survey?.id === surveyId;

  if (loading && (surveyResponse === null || !surveyMatchesRoute)) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Survey Details" />
        <LoadingState message="Loading survey details..." />
      </div>
    );
  }

  if (isNotFound) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Survey Details" />
        <EmptyState
          variant="info"
          title="Survey not found"
          message="The selected survey does not exist or may have been removed."
          action={{ label: "Back to Surveys", onClick: () => navigate(ROUTES.surveys.management) }}
        />
      </div>
    );
  }

  if (error || !survey) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Survey Details" />
        {error && <ErrorBanner error={error.message} onRetry={refetch} loading={isRefetching} />}
        <EmptyState
          variant="error"
          title="Unable to load survey details"
          message="Please retry or return to Surveys Management."
          action={{ label: "Back to Surveys", onClick: () => navigate(ROUTES.surveys.management) }}
        />
      </div>
    );
  }

  if (!draftSurvey) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Survey Details" />
        <LoadingState message="Preparing survey editor..." />
      </div>
    );
  }

  const closeQuestionModal = () => {
    setQuestionModal({ open: false, mode: "add", order: null });
    setEditingQuestion(null);
  };

  const openAddQuestionModal = () => {
    setQuestionModal({ open: true, mode: "add", order: null });
    setEditingQuestion(null);
  };

  const openEditQuestionModal = (question: FlattenedSurveyItem) => {
    setQuestionModal({ open: true, mode: "edit", order: question.order });
    setEditingQuestion(mapSurveyElementToLibraryQuestion(question.element));
  };

  const closeContentModal = () => {
    setContentModal({ open: false, mode: "add", kind: "header", order: null });
    setContentForm({ text: "", headerTag: DEFAULT_HEADER_TAG });
    setContentDisplayRules([]);
  };

  const openAddContentModal = (kind: Extract<SurveyItemKind, "header" | "freeText">) => {
    setContentModal({ open: true, mode: "add", kind, order: null });
    setContentForm({ text: "", headerTag: DEFAULT_HEADER_TAG });
    setContentDisplayRules([]);
  };

  const openEditContentModal = (item: FlattenedSurveyItem) => {
    const kind = item.kind === "header" ? "header" : "freeText";
    const htmlValue = typeof item.element.html === "string" ? item.element.html : "";
    const plainTextContent = htmlToPlainText(htmlValue);
    setContentModal({ open: true, mode: "edit", kind, order: item.order });
    setContentForm({
      text: extractSourceTitle(item.element.title) ?? plainTextContent,
      headerTag: kind === "header" ? parseHeaderTagFromHtml(htmlValue) : DEFAULT_HEADER_TAG,
    });
    setContentDisplayRules(
      Array.isArray(item.element.displayRules) ? item.element.displayRules : [],
    );
  };

  const handleQuestionSubmit = (question: CreateQuestionBody | UpdateQuestionBody) => {
    if (questionModal.mode === "add") {
      const newElement = mapQuestionPayloadToSurveyElement(question, draftSurvey.definition);
      const definition = appendElementToFirstPage(draftSurvey.definition, newElement);
      setDraftSurvey(prev => (prev ? { ...prev, definition } : prev));
      closeQuestionModal();
      return;
    }

    if (questionModal.order === null) {
      return;
    }

    setDraftSurvey(prev =>
      prev
        ? {
            ...prev,
            definition: updateElementAtOrder(prev.definition, questionModal.order as number, item =>
              mergeEditedQuestionElement(
                item,
                mapQuestionPayloadToSurveyElement(question, prev.definition, item.name),
              ),
            ),
          }
        : prev,
    );
    closeQuestionModal();
  };

  const clearSettingError = (field: string) => {
    setSettingsErrors(prev => {
      if (!prev[field]) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSaveAll = async () => {
    if (!surveyId) return;

    const newErrors: Record<string, string> = {};
    const trimmedName = draftSurvey.name.trim();
    if (!trimmedName) {
      newErrors.name = "Survey name is required";
    }
    if (!draftSurvey.editionId && !draftSurvey.edition) {
      newErrors.edition = "Edition is required";
    }
    if (!draftSurvey.editionId && !draftSurvey.publicationId) {
      newErrors.publication = "Publication is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setSettingsErrors(newErrors);
      setActiveTab("settings");
      setToast({ message: "Fix settings errors before saving.", type: "error" });
      return;
    }

    setSettingsErrors({});

    let currentTranslations = translationsByLanguage;
    if (selectedTranslationItem) {
      const translationKey = getTranslationItemKey(selectedTranslationItem);
      const sourceText = getItemSourceText(selectedTranslationItem);
      const updatedLanguageEntries = translatableLanguages.reduce(
        (acc, language) => {
          const value = translationEditorValues[language]?.trim() ?? "";
          const base = { ...(currentTranslations[language] ?? {}) };
          if (value) {
            base[translationKey] = { value, sourceSnapshot: sourceText };
          } else {
            delete base[translationKey];
          }
          return { ...acc, [language]: base };
        },
        {} as typeof translationsByLanguage,
      );
      currentTranslations = { ...currentTranslations, ...updatedLanguageEntries };

      if (hasTranslatableChoices(selectedTranslationItem.element)) {
        const choices = selectedTranslationItem.element.choices!;
        for (const language of translatableLanguages) {
          for (let choiceIdx = 0; choiceIdx < choices.length; choiceIdx++) {
            const choiceKey = getChoiceTranslationKey(selectedTranslationItem, choiceIdx);
            const choiceValue = choiceTranslationEditorValues[language]?.[choiceIdx]?.trim() ?? "";
            const base = { ...(currentTranslations[language] ?? {}) };
            if (choiceValue) {
              base[choiceKey] = {
                value: choiceValue,
                sourceSnapshot: getChoiceSourceText(choices[choiceIdx]),
              };
            } else {
              delete base[choiceKey];
            }
            currentTranslations = { ...currentTranslations, [language]: base };
          }
        }
      }

      if (hasTranslatableRangeLabels(selectedTranslationItem.element)) {
        const labels = [...selectedTranslationItem.element.rangeLabels!].sort(
          (a, b) => a.order - b.order,
        );
        for (const language of translatableLanguages) {
          for (const rl of labels) {
            const rlKey = getRangeLabelTranslationKey(selectedTranslationItem, rl.id);
            const rlValue = rangeLabelTranslationEditorValues[language]?.[rl.id]?.trim() ?? "";
            const base = { ...(currentTranslations[language] ?? {}) };
            if (rlValue) {
              base[rlKey] = {
                value: rlValue,
                sourceSnapshot: getRangeLabelSourceText(rl),
              };
            } else {
              delete base[rlKey];
            }
            currentTranslations = { ...currentTranslations, [language]: base };
          }
        }
      }

      if (hasTranslatableQuestionDescription(selectedTranslationItem)) {
        const descKey = getDescriptionTranslationKey(selectedTranslationItem);
        const descSource = getDescriptionSourceText(selectedTranslationItem.element);
        for (const language of translatableLanguages) {
          const descValue = descriptionTranslationEditorValues[language]?.trim() ?? "";
          const base = { ...(currentTranslations[language] ?? {}) };
          if (descValue) {
            base[descKey] = {
              value: descValue,
              sourceSnapshot: descSource,
            };
          } else {
            delete base[descKey];
          }
          currentTranslations = { ...currentTranslations, [language]: base };
        }
      }

      for (const field of getTranslatableLabelFields(selectedTranslationItem)) {
        const labelKey = getLabelFieldTranslationKey(selectedTranslationItem, field);
        const labelSource = getLabelFieldSourceText(selectedTranslationItem.element, field);
        for (const language of translatableLanguages) {
          const labelValue = labelFieldTranslationEditorValues[field.key]?.[language]?.trim() ?? "";
          const base = { ...(currentTranslations[language] ?? {}) };
          if (labelValue) {
            base[labelKey] = {
              value: labelValue,
              sourceSnapshot: labelSource,
            };
          } else {
            delete base[labelKey];
          }
          currentTranslations = { ...currentTranslations, [language]: base };
        }
      }

      setTranslationsByLanguage(currentTranslations);
    }

    let currentFooterTranslations = { ...footerTranslationsByLanguage };
    if (selectedTranslation?.kind === "footer" && hasFooterToTranslate) {
      for (const language of translatableLanguages) {
        const value = footerTranslationEditorValues[language]?.trim() ?? "";
        if (value) {
          currentFooterTranslations = {
            ...currentFooterTranslations,
            [language]: { value, sourceSnapshot: footerSourceText },
          };
        } else {
          const next = { ...currentFooterTranslations };
          delete next[language];
          currentFooterTranslations = next;
        }
      }
      setFooterTranslationsByLanguage(currentFooterTranslations);
    }

    let currentHeaderTranslations = { ...headerTranslationsByLanguage };
    if (selectedTranslation?.kind === "header" && hasHeaderToTranslate) {
      for (const language of translatableLanguages) {
        const value = headerTranslationEditorValues[language]?.trim() ?? "";
        if (value) {
          currentHeaderTranslations = {
            ...currentHeaderTranslations,
            [language]: { value, sourceSnapshot: headerSourceText },
          };
        } else {
          const next = { ...currentHeaderTranslations };
          delete next[language];
          currentHeaderTranslations = next;
        }
      }
      setHeaderTranslationsByLanguage(currentHeaderTranslations);
    }

    const mergedDefinition = mergeTranslationsIntoDefinition(
      draftSurvey.definition,
      currentTranslations,
      flattenedItems,
      translatableLanguages,
    );
    const mergedWithFooter = mergeFooterTranslationsIntoDefinition(
      mergedDefinition,
      currentFooterTranslations,
    );
    const mergedWithBoth = mergeHeaderTranslationsIntoDefinition(
      mergedWithFooter,
      currentHeaderTranslations,
    );

    const editionFields = toSurveyEditionFields(draftSurvey);
    if (!editionFields) return;

    const payload: UpdateSurveyBody = {
      name: trimmedName,
      ...editionFields,
      defaultLanguage: draftSurvey.defaultLanguage,
      randomQuestionCount: 3,
      status: draftSurvey.status,
      version: draftSurvey.version,
      lockVersion: draftSurvey.lockVersion,
      definition: prepareDefinitionForSave(mergedWithBoth),
      // Empty condition arrays are omitted — omitted and empty both mean
      // "general survey" per the API contract. Collapsing the section does not
      // discard conditions; only unchecking every box reverts to general.
      ...surveyConditionsToPayloadFields(effectiveConditions),
    };

    try {
      const response = await updateSurvey({ id: surveyId, body: payload });
      setDraftSurvey(toEditableSurveyState(response.survey));
      setSelectedEditionYear(response.survey.edition?.match(/\d{4}/)?.[0] ?? "");
      const items = flattenSurveyItems(toSurveyDefinition(response.survey.definition as object));
      const extracted = extractTranslationsFromRawDefinition(
        response.survey.definition as object,
        items,
        supportedLanguages,
      );
      setTranslationsByLanguage(extracted);
      const footerExtracted = extractFooterTranslationsFromRawDefinition(
        response.survey.definition as object,
        supportedLanguages,
      );
      setFooterTranslationsByLanguage(footerExtracted);
      const headerExtracted = extractHeaderTranslationsFromRawDefinition(
        response.survey.definition as object,
        supportedLanguages,
      );
      setHeaderTranslationsByLanguage(headerExtracted);
      setToast({ message: "Saved.", type: "success" });
    } catch (saveError) {
      const apiError = saveError as ApiError;
      if (isSurveyEditionPublicationTakenConflict(apiError)) {
        // Prefer the API's message — it names the conflicting conditions.
        setToast({ message: apiError.message || DUPLICATE_SURVEY_CONFLICT_MESSAGE, type: "error" });
        return;
      }
      if (apiError.status === 409) {
        setToast({
          message:
            "Another change was saved first. Refresh the survey and retry your update with the latest version.",
          type: "error",
        });
        return;
      }
      setToast({
        message: apiError.message || "Failed to save survey.",
        type: "error",
      });
    }
  };

  // Shared publish routine reused by both the standard publish confirmation
  // and the no-testimonial warning modal. Performs the actual API call and
  // closes whichever modal triggered it on success/missing-translations.
  const doPublish = async () => {
    if (!surveyId) return;

    try {
      const response = await publishSurvey(surveyId);
      setDraftSurvey(toEditableSurveyState(response.survey));
      setSelectedEditionYear(response.survey.edition?.match(/\d{4}/)?.[0] ?? "");
      setPublishConfirmOpen(false);
      setTestimonialWarningOpen(false);
      setPublishValidationError(null);
      setToast({ message: "Survey published successfully.", type: "success" });
    } catch (publishError) {
      const apiError = publishError as ApiError;
      if (
        apiError.status === 400 &&
        apiError.detailsRaw &&
        typeof apiError.detailsRaw === "object"
      ) {
        const raw = apiError.detailsRaw as { missingTranslations?: unknown };
        if (Array.isArray(raw.missingTranslations) && raw.missingTranslations.length > 0) {
          setPublishConfirmOpen(false);
          setTestimonialWarningOpen(false);
          setServerMissingTranslations(raw.missingTranslations as BackendMissingTranslation[]);
          return;
        }
      }
      if (apiError.status === 409) {
        setToast({
          message:
            "Unable to publish this survey. It may already be published, or another survey for this edition and publication is already published.",
          type: "error",
        });
        return;
      }

      setToast({
        message: apiError.message || "Failed to publish survey. Please try again.",
        type: "error",
      });
    }
  };

  const handlePublishConfirm = async () => {
    if (!surveyId) return;
    if (hasMissingTranslations) {
      setPublishConfirmOpen(false);
      setPublishValidationError(publishBlockedMessage);
      setToast({
        message: publishBlockedMessage,
        type: "error",
      });
      return;
    }

    await doPublish();
  };

  const handlePublishClick = () => {
    if (hasMissingTranslations) {
      setPublishValidationError(publishBlockedMessage);
      setToast({
        message: publishBlockedMessage,
        type: "error",
      });
      return;
    }

    // Soften the testimonial requirement: instead of hard-blocking, surface a
    // warning modal that lets the user proceed with publishing or go back.
    if (countTestimonialQuestions(draftSurvey.definition) < 1) {
      setPublishValidationError(null);
      setTestimonialWarningOpen(true);
      return;
    }

    setPublishValidationError(null);
    setPublishConfirmOpen(true);
  };

  const handleContentSubmit = () => {
    const flushedRules = contentDisplayRulesEditorRef.current?.flushPendingRule() ?? null;
    const effectiveContentDisplayRules = flushedRules ?? contentDisplayRules;

    const text = contentForm.text.trim();
    if (!text) {
      setToast({
        message:
          contentModal.kind === "header"
            ? "Header text is required."
            : "Free text content is required.",
        type: "error",
      });
      return;
    }

    const headerTag = contentForm.headerTag;
    if (contentModal.mode === "add") {
      const element = {
        ...buildContentElement(draftSurvey.definition, contentModal.kind, text, headerTag),
        displayRules:
          contentModal.kind === "header" && effectiveContentDisplayRules.length > 0
            ? effectiveContentDisplayRules
            : undefined,
      };
      const definition = appendElementToFirstPage(draftSurvey.definition, element);
      setDraftSurvey(prev => (prev ? { ...prev, definition } : prev));
      closeContentModal();
      return;
    }

    if (contentModal.order === null) {
      return;
    }
    const targetOrder = contentModal.order;

    setDraftSurvey(prev =>
      prev
        ? {
            ...prev,
            definition: updateElementAtOrder(prev.definition, targetOrder, item => ({
              ...item,
              ...buildContentElement(prev.definition, contentModal.kind, text, headerTag),
              name: item.name,
              displayRules:
                contentModal.kind === "header"
                  ? effectiveContentDisplayRules.length > 0
                    ? effectiveContentDisplayRules
                    : undefined
                  : item.displayRules,
            })),
          }
        : prev,
    );
    closeContentModal();
  };

  const handleAddFromLibrary = (libraryQuestion: LibraryQuestion) => {
    const element = mapLibraryQuestionToSurveyElement(libraryQuestion, draftSurvey.definition);
    const definition = appendElementToFirstPage(draftSurvey.definition, element);
    setDraftSurvey(prev => (prev ? { ...prev, definition } : prev));
    setLibraryModalOpen(false);
    setToast({ message: "Added question from library.", type: "success" });
  };

  // A divider has no text to edit, so it is inserted directly without opening
  // the content modal. It appends to page 0, matching every other "add" path.
  const handleAddDivider = () => {
    const element = buildDividerElement(draftSurvey.definition);
    const definition = appendElementToFirstPage(draftSurvey.definition, element);
    setDraftSurvey(prev => (prev ? { ...prev, definition } : prev));
  };

  const saveTranslationForItem = (item: FlattenedSurveyItem, language: string, value: string) => {
    const translationKey = getTranslationItemKey(item);
    const sourceText = getItemSourceText(item);

    setTranslationsByLanguage(prev => ({
      ...prev,
      [language]: {
        ...(prev[language] ?? {}),
        [translationKey]: {
          value,
          sourceSnapshot: sourceText,
        },
      },
    }));
  };

  const handleAutoTranslateItemForLanguage = async (
    item: FlattenedSurveyItem,
    language: string,
  ) => {
    const sourceText = getItemSourceText(item);
    try {
      const { translatedText } = await surveysService.translate(sourceText, language);
      saveTranslationForItem(item, language, translatedText);
      setTranslationEditorValues(prev => ({ ...prev, [language]: translatedText }));
    } catch {
      setToast({ message: `Auto-translate to ${language.toUpperCase()} failed.`, type: "error" });
    }

    if (hasTranslatableQuestionDescription(item)) {
      const descriptionSource = getDescriptionSourceText(item.element);
      try {
        const { translatedText: descriptionTranslated } = await surveysService.translate(
          descriptionSource,
          language,
        );
        const descKey = getDescriptionTranslationKey(item);
        setTranslationsByLanguage(prev => ({
          ...prev,
          [language]: {
            ...(prev[language] ?? {}),
            [descKey]: { value: descriptionTranslated, sourceSnapshot: descriptionSource },
          },
        }));
        setDescriptionTranslationEditorValues(prev => ({
          ...prev,
          [language]: descriptionTranslated,
        }));
      } catch {
        setToast({
          message: `Description auto-translate to ${language.toUpperCase()} failed.`,
          type: "error",
        });
      }
    }

    for (const field of getTranslatableLabelFields(item)) {
      const labelSource = getLabelFieldSourceText(item.element, field);
      try {
        const { translatedText: labelTranslated } = await surveysService.translate(
          labelSource,
          language,
        );
        const labelKey = getLabelFieldTranslationKey(item, field);
        setTranslationsByLanguage(prev => ({
          ...prev,
          [language]: {
            ...(prev[language] ?? {}),
            [labelKey]: {
              value: labelTranslated,
              sourceSnapshot: labelSource,
            },
          },
        }));
        setLabelFieldTranslationEditorValues(prev => ({
          ...prev,
          [field.key]: { ...(prev[field.key] ?? {}), [language]: labelTranslated },
        }));
      } catch {
        setToast({
          message: `${field.displayName} auto-translate to ${language.toUpperCase()} failed.`,
          type: "error",
        });
      }
    }

    if (hasTranslatableChoices(item.element)) {
      const choices = item.element.choices!;
      const results = await Promise.allSettled(
        choices.map((choice, idx) =>
          surveysService
            .translate(getChoiceSourceText(choice), language)
            .then(({ translatedText }) => ({
              idx,
              translatedText,
              sourceSnapshot: getChoiceSourceText(choice),
            })),
        ),
      );
      setTranslationsByLanguage(prev => {
        const next = { ...prev };
        for (const result of results) {
          if (result.status !== "fulfilled") continue;
          const { idx, translatedText, sourceSnapshot } = result.value;
          const choiceKey = getChoiceTranslationKey(item, idx);
          next[language] = {
            ...(next[language] ?? {}),
            [choiceKey]: { value: translatedText, sourceSnapshot },
          };
        }
        return next;
      });
      setChoiceTranslationEditorValues(prev => {
        const next = { ...prev };
        const langMap = { ...(next[language] ?? {}) };
        for (const result of results) {
          if (result.status !== "fulfilled") continue;
          langMap[result.value.idx] = result.value.translatedText;
        }
        next[language] = langMap;
        return next;
      });
    }

    if (hasTranslatableRangeLabels(item.element)) {
      const rangeLabels = [...item.element.rangeLabels!].sort((a, b) => a.order - b.order);
      const rangeResults = await Promise.allSettled(
        rangeLabels.map(rl =>
          surveysService
            .translate(getRangeLabelSourceText(rl), language)
            .then(({ translatedText }) => ({
              id: rl.id,
              translatedText,
              sourceSnapshot: getRangeLabelSourceText(rl),
            })),
        ),
      );
      setTranslationsByLanguage(prev => {
        const next = { ...prev };
        for (const result of rangeResults) {
          if (result.status !== "fulfilled") continue;
          const { id, translatedText, sourceSnapshot } = result.value;
          const rlKey = getRangeLabelTranslationKey(item, id);
          next[language] = {
            ...(next[language] ?? {}),
            [rlKey]: { value: translatedText, sourceSnapshot },
          };
        }
        return next;
      });
      setRangeLabelTranslationEditorValues(prev => {
        const next = { ...prev };
        const langMap = { ...(next[language] ?? {}) };
        for (const result of rangeResults) {
          if (result.status !== "fulfilled") continue;
          langMap[result.value.id] = result.value.translatedText;
        }
        next[language] = langMap;
        return next;
      });
    }

    if (hasTranslatableQuestionDescription(item)) {
      const descSource = getDescriptionSourceText(item.element);
      try {
        const { translatedText } = await surveysService.translate(descSource, language);
        const descKey = getDescriptionTranslationKey(item);
        setTranslationsByLanguage(prev => ({
          ...prev,
          [language]: {
            ...(prev[language] ?? {}),
            [descKey]: { value: translatedText, sourceSnapshot: descSource },
          },
        }));
        setDescriptionTranslationEditorValues(prev => ({ ...prev, [language]: translatedText }));
      } catch {
        setToast({
          message: `Auto-translate description to ${language.toUpperCase()} failed.`,
          type: "error",
        });
      }
    }
  };

  const handleAutoTranslateSelectedItemAllLanguages = async () => {
    if (!selectedTranslationItem) {
      return;
    }

    setAutoTranslateLoading(true);
    for (const language of translatableLanguages) {
      await handleAutoTranslateItemForLanguage(selectedTranslationItem, language);
    }
    setAutoTranslateLoading(false);
    setToast({ message: "Item auto-translated in all languages.", type: "success" });
  };

  const handleAutoTranslateFooterForLanguage = async (language: string) => {
    if (!hasFooterToTranslate) return;
    try {
      const { translatedText } = await surveysService.translate(footerSourceText, language);
      setFooterTranslationsByLanguage(prev => ({
        ...prev,
        [language]: { value: translatedText, sourceSnapshot: footerSourceText },
      }));
      setFooterTranslationEditorValues(prev => ({ ...prev, [language]: translatedText }));
    } catch {
      setToast({
        message: `Auto-translate to ${language.toUpperCase()} failed.`,
        type: "error",
      });
    }
  };

  const handleAutoTranslateFooterAllLanguages = async () => {
    if (!hasFooterToTranslate) return;
    setAutoTranslateLoading(true);
    for (const language of translatableLanguages) {
      await handleAutoTranslateFooterForLanguage(language);
    }
    setAutoTranslateLoading(false);
    setToast({ message: "Footer auto-translated in all languages.", type: "success" });
  };

  const handleAutoTranslateHeaderForLanguage = async (language: string) => {
    if (!hasHeaderToTranslate) return;
    try {
      const { translatedText } = await surveysService.translate(headerSourceText, language);
      setHeaderTranslationsByLanguage(prev => ({
        ...prev,
        [language]: { value: translatedText, sourceSnapshot: headerSourceText },
      }));
      setHeaderTranslationEditorValues(prev => ({ ...prev, [language]: translatedText }));
    } catch {
      setToast({
        message: `Auto-translate to ${language.toUpperCase()} failed.`,
        type: "error",
      });
    }
  };

  const handleAutoTranslateHeaderAllLanguages = async () => {
    if (!hasHeaderToTranslate) return;
    setAutoTranslateLoading(true);
    for (const language of translatableLanguages) {
      await handleAutoTranslateHeaderForLanguage(language);
    }
    setAutoTranslateLoading(false);
    setToast({ message: "Header auto-translated in all languages.", type: "success" });
  };

  type TranslationNavUnit =
    | { kind: "header" }
    | { kind: "footer" }
    | { kind: "item"; item: FlattenedSurveyItem };

  const buildTranslationNavUnits = (): TranslationNavUnit[] => {
    const units: TranslationNavUnit[] = [];
    if (hasHeaderToTranslate) {
      units.push({ kind: "header" });
    }
    for (const item of translatableItems) {
      units.push({ kind: "item", item });
    }
    if (hasFooterToTranslate) {
      units.push({ kind: "footer" });
    }
    return units;
  };

  const isNavUnitFullyTranslated = (unit: TranslationNavUnit): boolean => {
    if (!draftSurvey) {
      return true;
    }
    const def = draftSurvey.definition;
    if (unit.kind === "header") {
      return isHeaderFullyTranslated(def, headerTranslationsByLanguage, translatableLanguages);
    }
    if (unit.kind === "footer") {
      return isFooterFullyTranslated(def, footerTranslationsByLanguage, translatableLanguages);
    }
    return isItemFullyTranslated(unit.item);
  };

  const getCurrentTranslationNavIndex = (units: TranslationNavUnit[]): number => {
    if (selectedTranslation?.kind === "header") {
      return units.findIndex(u => u.kind === "header");
    }
    if (selectedTranslation?.kind === "footer") {
      return units.findIndex(u => u.kind === "footer");
    }
    if (selectedTranslation?.kind === "item" && selectedTranslationItem) {
      const key = getTranslationItemKey(selectedTranslationItem);
      return units.findIndex(u => u.kind === "item" && getTranslationItemKey(u.item) === key);
    }
    return -1;
  };

  const selectTranslationNavUnit = (unit: TranslationNavUnit) => {
    if (unit.kind === "header") {
      setSelectedTranslation({ kind: "header" });
    } else if (unit.kind === "footer") {
      setSelectedTranslation({ kind: "footer" });
    } else {
      setSelectedTranslation({ kind: "item", key: getTranslationItemKey(unit.item) });
    }
  };

  const handleNextMissing = () => {
    const units = buildTranslationNavUnits();
    if (units.length === 0) {
      return;
    }

    const currentIndex = getCurrentTranslationNavIndex(units);
    const orderedUnits = [...units.slice(currentIndex + 1), ...units.slice(0, currentIndex + 1)];

    const nextMissing = orderedUnits.find(u => !isNavUnitFullyTranslated(u));

    if (nextMissing) {
      selectTranslationNavUnit(nextMissing);
      return;
    }

    setToast({ message: "All items are translated in target languages.", type: "success" });
  };

  const handlePreviousMissing = () => {
    const units = buildTranslationNavUnits();
    if (units.length === 0) {
      return;
    }

    const currentIndex = getCurrentTranslationNavIndex(units);

    const orderedUnits =
      currentIndex < 0
        ? [...units].reverse()
        : [...units.slice(0, currentIndex).reverse(), ...units.slice(currentIndex).reverse()];

    const prevMissing = orderedUnits.find(u => !isNavUnitFullyTranslated(u));

    if (prevMissing) {
      selectTranslationNavUnit(prevMissing);
      return;
    }

    setToast({ message: "All items are translated in target languages.", type: "success" });
  };

  const handleAutoTranslateAll = async () => {
    if (translatableLanguages.length === 0) {
      return;
    }

    setAutoTranslateLoading(true);

    const jobs: Array<{
      target: "item" | "header" | "footer";
      key: string;
      language: string;
      sourceText: string;
    }> = [];
    const pushItemJob = (key: string, language: string, sourceText: string) => {
      jobs.push({ target: "item", key, language, sourceText });
    };
    const isStale = (language: string, key: string, sourceText: string) => {
      const entry = translationsByLanguage[language]?.[key];
      return !entry?.value?.trim() || entry.sourceSnapshot !== sourceText;
    };

    for (const item of translatableItems) {
      for (const language of translatableLanguages) {
        if (getTranslationStatus(item, language) !== "translated") {
          pushItemJob(getTranslationItemKey(item), language, getItemSourceText(item));
        }
      }
    }

    for (const item of translatableItems) {
      if (!hasTranslatableChoices(item.element)) continue;
      const choices = item.element.choices!;
      for (const language of translatableLanguages) {
        for (let idx = 0; idx < choices.length; idx++) {
          const choiceKey = getChoiceTranslationKey(item, idx);
          const sourceText = getChoiceSourceText(choices[idx]);
          if (isStale(language, choiceKey, sourceText)) {
            pushItemJob(choiceKey, language, sourceText);
          }
        }
      }
    }

    for (const item of translatableItems) {
      if (!hasTranslatableRangeLabels(item.element)) continue;
      const labels = [...item.element.rangeLabels!].sort((a, b) => a.order - b.order);
      for (const language of translatableLanguages) {
        for (const rl of labels) {
          const rlKey = getRangeLabelTranslationKey(item, rl.id);
          const sourceText = getRangeLabelSourceText(rl);
          if (isStale(language, rlKey, sourceText)) {
            pushItemJob(rlKey, language, sourceText);
          }
        }
      }
    }

    for (const item of translatableItems) {
      if (!hasTranslatableQuestionDescription(item)) continue;
      for (const language of translatableLanguages) {
        const descKey = getDescriptionTranslationKey(item);
        const sourceText = getDescriptionSourceText(item.element);
        if (isStale(language, descKey, sourceText)) {
          pushItemJob(descKey, language, sourceText);
        }
      }
    }

    for (const item of translatableItems) {
      for (const field of getTranslatableLabelFields(item)) {
        const labelKey = getLabelFieldTranslationKey(item, field);
        const sourceText = getLabelFieldSourceText(item.element, field);
        for (const language of translatableLanguages) {
          if (isStale(language, labelKey, sourceText)) {
            pushItemJob(labelKey, language, sourceText);
          }
        }
      }
    }

    if (hasHeaderToTranslate) {
      for (const language of translatableLanguages) {
        const existing = headerTranslationsByLanguage[language];
        const isFresh =
          existing && existing.sourceSnapshot === headerSourceText && existing.value.trim();
        if (isFresh) continue;
        jobs.push({ target: "header", key: "header", language, sourceText: headerSourceText });
      }
    }

    if (hasFooterToTranslate) {
      for (const language of translatableLanguages) {
        const existing = footerTranslationsByLanguage[language];
        const isFresh =
          existing && existing.sourceSnapshot === footerSourceText && existing.value.trim();
        if (isFresh) continue;
        jobs.push({ target: "footer", key: "footer", language, sourceText: footerSourceText });
      }
    }

    const jobsByLanguage = new Map<string, typeof jobs>();
    for (const job of jobs) {
      if (!job.sourceText.trim()) continue;
      const languageJobs = jobsByLanguage.get(job.language) ?? [];
      languageJobs.push(job);
      jobsByLanguage.set(job.language, languageJobs);
    }
    const chunks: Array<typeof jobs> = [];
    for (const languageJobs of jobsByLanguage.values()) {
      let chunk: typeof jobs = [];
      let chunkChars = 0;
      for (const job of languageJobs) {
        const isFull =
          chunk.length >= AUTO_TRANSLATE_BATCH_SIZE ||
          chunkChars + job.sourceText.length > AUTO_TRANSLATE_BATCH_MAX_CHARS;
        if (chunk.length > 0 && isFull) {
          chunks.push(chunk);
          chunk = [];
          chunkChars = 0;
        }
        chunk.push(job);
        chunkChars += job.sourceText.length;
      }
      if (chunk.length > 0) chunks.push(chunk);
    }

    const translated: Array<{ job: (typeof jobs)[number]; translatedText: string }> = [];
    let failCount = 0;
    const failedLanguages = new Set<string>();
    let nextChunkIndex = 0;
    const runChunks = async () => {
      while (nextChunkIndex < chunks.length) {
        const chunk = chunks[nextChunkIndex];
        nextChunkIndex += 1;
        const language = chunk[0].language;
        try {
          const { translations } = await surveysService.translateBatch(
            chunk.map(job => job.sourceText),
            language,
          );
          if (translations.length !== chunk.length) {
            throw new Error(`Expected ${chunk.length} translations, got ${translations.length}`);
          }
          chunk.forEach((job, idx) => {
            translated.push({ job, translatedText: translations[idx] });
          });
        } catch (error) {
          failCount += chunk.length;
          failedLanguages.add(language);
          console.error("Auto-translate batch failed", { language, error });
        }
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(AUTO_TRANSLATE_CONCURRENCY, chunks.length) }, runChunks),
    );

    setTranslationsByLanguage(prev => {
      const next = { ...prev };
      for (const { job, translatedText } of translated) {
        if (job.target !== "item") continue;
        next[job.language] = {
          ...(next[job.language] ?? {}),
          [job.key]: { value: translatedText, sourceSnapshot: job.sourceText },
        };
      }
      return next;
    });
    setHeaderTranslationsByLanguage(prev => {
      const next = { ...prev };
      for (const { job, translatedText } of translated) {
        if (job.target !== "header") continue;
        next[job.language] = { value: translatedText, sourceSnapshot: job.sourceText };
      }
      return next;
    });
    setFooterTranslationsByLanguage(prev => {
      const next = { ...prev };
      for (const { job, translatedText } of translated) {
        if (job.target !== "footer") continue;
        next[job.language] = { value: translatedText, sourceSnapshot: job.sourceText };
      }
      return next;
    });

    setAutoTranslateLoading(false);
    if (failCount > 0) {
      setToast({
        message: `Auto-translate complete. ${failCount} item(s) failed (${[...failedLanguages].sort().join(", ")}).`,
        type: "error",
      });
    } else {
      setToast({ message: "All missing items auto-translated.", type: "success" });
    }
  };

  const getQuestionDragId = (item: FlattenedSurveyItem): string =>
    `${item.element.name}::${item.order}`;

  const handleQuestionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const activeId = String(active.id);
    const overId = String(over.id);
    const sourceOrder = flattenedItems.findIndex(item => getQuestionDragId(item) === activeId);
    const destinationOrder = flattenedItems.findIndex(item => getQuestionDragId(item) === overId);

    if (sourceOrder < 0 || destinationOrder < 0) {
      return;
    }

    setDraftSurvey(prev =>
      prev
        ? {
            ...prev,
            definition: moveElementToIndex(prev.definition, sourceOrder, destinationOrder),
          }
        : prev,
    );
  };

  return (
    <div className={styles.wrapper}>
      <PageHeader title={draftSurvey.name} subtitle={subtitle}>
        <div className={styles.headerActions}>
          <Chip
            size="small"
            color={STATUS_CHIP_COLOR[draftSurvey.status]}
            className={draftSurvey.status === "draft" ? styles.draftStatusChip : undefined}
          >
            {draftSurvey.status.charAt(0).toUpperCase() + draftSurvey.status.slice(1)}
          </Chip>
          {draftSurvey.status !== "draft" && (
            <Chip size="small" variant="outlined" color="primary">
              v{draftSurvey.version}
            </Chip>
          )}
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate(ROUTES.surveys.management)}
          >
            Back to Surveys
          </Button>
        </div>
      </PageHeader>

      <div className={styles.tabList} role="tablist" aria-label="Survey sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "questions"}
          className={activeTab === "questions" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("questions")}
        >
          Content
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "translations"}
          className={activeTab === "translations" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("translations")}
        >
          Translations
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "preview"}
          className={activeTab === "preview" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("preview")}
        >
          Preview
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "settings"}
          className={activeTab === "settings" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("settings")}
        >
          Settings
        </button>
        <div className={styles.tabListActions}>
          {draftSurvey.status === "draft" && (
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              onClick={handlePublishClick}
              disabled={publishLoading}
            >
              {publishLoading ? "Publishing..." : "Publish"}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            size="medium"
            className={styles.saveButton}
            onClick={handleSaveAll}
            disabled={saveLoading}
          >
            {saveLoading ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      <div
        className={
          activeTab === "translations"
            ? `${styles.tabPanel} ${styles.tabPanelTranslations}`
            : activeTab === "preview"
              ? `${styles.tabPanel} ${styles.tabPanelPreview}`
              : styles.tabPanel
        }
      >
        {activeTab === "questions" && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3>Introduction (optional)</h3>
            </div>
            <p className={styles.inlineHint}>
              Shown above all questions when the referee opens the survey.
            </p>
            <textarea
              className={styles.sectionTextarea}
              value={getHeaderText(draftSurvey.definition)}
              placeholder="e.g. Welcome to the Legal 500 referee survey."
              onChange={event => {
                const nextText = event.target.value;
                setDraftSurvey(prev =>
                  prev ? { ...prev, definition: setHeaderText(prev.definition, nextText) } : prev,
                );
              }}
            />
            <div className={styles.sectionHeader}>
              <h3>Content</h3>
              <div className={styles.questionActions}>
                <Button variant="outlined" size="small" onClick={() => setLibraryModalOpen(true)}>
                  Pick from Library
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={openAddQuestionModal}
                  startIcon={<Icon icon="plus" size="small" />}
                >
                  Custom Question
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => openAddContentModal("header")}
                  startIcon={<Icon icon="plus" size="small" />}
                >
                  Add Header
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => openAddContentModal("freeText")}
                  startIcon={<Icon icon="plus" size="small" />}
                >
                  Add Free Text
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAddDivider}
                  startIcon={<Icon icon="plus" size="small" />}
                >
                  Add Divider
                </Button>
              </div>
            </div>
            <p className={styles.inlineHint}>
              Build the flow with reusable library questions, custom questions, headers, and
              supporting free-text blocks.
            </p>

            {flattenedItems.length === 0 ? (
              <EmptyState
                variant="empty"
                title="No questions yet"
                message="Start by adding a library question, custom question, header, or free text block."
              />
            ) : (
              <DndContext
                sensors={dragSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleQuestionDragEnd}
              >
                <SortableContext
                  items={flattenedItems.map(getQuestionDragId)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className={styles.questionList}>
                    {flattenedItems.map(question => (
                      <SortableQuestionCard
                        key={getQuestionDragId(question)}
                        id={getQuestionDragId(question)}
                      >
                        <div className={styles.questionInfo}>
                          <div className={styles.questionTitleRow}>
                            <span className={styles.questionTitle}>
                              {question.kind === "divider"
                                ? "Divider"
                                : (extractSourceTitle(question.element.title) ??
                                  question.element.name)}
                            </span>
                            <div className={styles.questionRowActions}>
                              {/* A divider has no editable content, so only reorder + delete apply. */}
                              {question.kind !== "divider" && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  className={styles.questionActionButton}
                                  aria-label="Edit question"
                                  onPointerDownCapture={stopDragPropagation}
                                  onMouseDownCapture={stopDragPropagation}
                                  onTouchStartCapture={stopDragPropagation}
                                  onClick={() =>
                                    question.kind === "question"
                                      ? openEditQuestionModal(question)
                                      : openEditContentModal(question)
                                  }
                                >
                                  <Icon icon="edit" size="small" />
                                </Button>
                              )}
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                className={styles.questionActionDeleteButton}
                                aria-label="Remove question"
                                onPointerDownCapture={stopDragPropagation}
                                onMouseDownCapture={stopDragPropagation}
                                onTouchStartCapture={stopDragPropagation}
                                onClick={() =>
                                  setDraftSurvey(prev =>
                                    prev
                                      ? {
                                          ...prev,
                                          definition: removeElementAtOrder(
                                            prev.definition,
                                            question.order,
                                          ),
                                        }
                                      : prev,
                                  )
                                }
                              >
                                <Icon icon="delete" size="small" />
                              </Button>
                            </div>
                          </div>
                          {question.kind === "freeText" && (
                            <span className={styles.questionDescription}>
                              {typeof question.element.html === "string"
                                ? htmlToPlainText(question.element.html)
                                : "Paragraph content"}
                            </span>
                          )}
                          {question.kind === "divider" && <hr className={styles.dividerPreview} />}
                          <div className={styles.questionMetaBlock}>
                            <div className={styles.questionMeta}>
                              <Chip size="small" variant="outlined" color="primary">
                                {getItemKindLabel(question)}
                              </Chip>
                              {question.kind === "question" && (
                                <Chip size="small" variant="outlined" color="secondary">
                                  {getSurveyQuestionTypeLabel(question.element)}
                                </Chip>
                              )}
                              {question.kind === "question" && question.element.isRequired && (
                                <Chip size="small" variant="outlined" color="error">
                                  Required
                                </Chip>
                              )}
                            </div>
                            {Array.isArray(question.element.displayRules) &&
                              question.element.displayRules.length > 0 && (
                                <div className={styles.questionRules}>
                                  <span className={styles.questionRulesLabel}>Display Rules</span>
                                  {renderDisplayRulesCompact(
                                    question.element.displayRules,
                                    ruleValueLabelMaps,
                                  )}
                                </div>
                              )}
                          </div>
                        </div>
                      </SortableQuestionCard>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
            <div className={styles.sectionHeader}>
              <h3>Footer (optional)</h3>
            </div>
            <p className={styles.inlineHint}>
              Shown below all questions when the referee fills out the survey.
            </p>
            <textarea
              className={styles.sectionTextarea}
              value={getFooterText(draftSurvey.definition)}
              placeholder="e.g. Thanks for taking the time to fill out this survey."
              onChange={event => {
                const nextText = event.target.value;
                setDraftSurvey(prev =>
                  prev ? { ...prev, definition: setFooterText(prev.definition, nextText) } : prev,
                );
              }}
            />
          </div>
        )}
        {activeTab === "translations" && (
          <div className={`${styles.section} ${styles.translationSection}`}>
            <div className={styles.translationToolbars}>
              <div className={styles.translationNavRow}>
                <span className={styles.translationProgress}>
                  {translationProgress.translated} / {translationProgress.total} translated
                </span>
                <div className={styles.translationNavSpacer} aria-hidden="true" />
                <Button variant="outlined" size="small" onClick={handlePreviousMissing}>
                  Previous missing
                </Button>
                <Button variant="outlined" size="small" onClick={handleNextMissing}>
                  Next missing
                </Button>
              </div>
              <div className={styles.translationActionsRow}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAutoTranslateAll}
                  disabled={translatableItems.length === 0 || autoTranslateLoading}
                >
                  {autoTranslateLoading ? "Translating..." : "Auto-translate all"}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    if (selectedTranslation?.kind === "header") {
                      handleAutoTranslateHeaderAllLanguages();
                    } else if (selectedTranslation?.kind === "footer") {
                      handleAutoTranslateFooterAllLanguages();
                    } else {
                      handleAutoTranslateSelectedItemAllLanguages();
                    }
                  }}
                  disabled={autoTranslateLoading || selectedTranslation === null}
                >
                  {autoTranslateLoading ? "Translating..." : "Auto-translate item (all languages)"}
                </Button>
              </div>
            </div>

            {translatableItems.length === 0 ? (
              <EmptyState
                variant="empty"
                title="No survey items to translate"
                message="Add questions, headers, or free text in the Questions tab first."
              />
            ) : translatableLanguages.length === 0 ? (
              <EmptyState
                variant="info"
                title="No target languages configured"
                message="Set at least one non-source language to begin translating survey content."
              />
            ) : (
              <div className={styles.translationWorkspace}>
                <div className={styles.translationListPane}>
                  <div className={styles.translationListScroll}>
                    {hasHeaderToTranslate && (
                      <>
                        <div className={styles.translationPaneHeader}>Header</div>
                        <button
                          type="button"
                          className={
                            selectedTranslation?.kind === "header"
                              ? styles.translationListItemActive
                              : styles.translationListItem
                          }
                          onClick={() => setSelectedTranslation({ kind: "header" })}
                        >
                          <span className={styles.translationListTitle}>{headerSourceText}</span>
                          <span className={styles.translationListMeta}>
                            <span>Header</span>
                            {(() => {
                              const headerStatus = getSectionTranslationStatus(
                                draftSurvey.definition,
                                HEADER_SECTION,
                                headerTranslationsByLanguage,
                                translatableLanguages,
                              );
                              return (
                                <span
                                  className={
                                    headerStatus === "translated"
                                      ? styles.statusTranslated
                                      : headerStatus === "outdated"
                                        ? styles.statusOutdated
                                        : styles.statusMissing
                                  }
                                >
                                  {headerStatus === "translated"
                                    ? "Translated"
                                    : headerStatus === "outdated"
                                      ? "Outdated"
                                      : "Missing"}
                                </span>
                              );
                            })()}
                          </span>
                        </button>
                      </>
                    )}
                    <div
                      className={
                        hasHeaderToTranslate
                          ? `${styles.translationPaneHeader} ${styles.translationSubsectionHeader}`
                          : styles.translationPaneHeader
                      }
                    >
                      Content
                    </div>
                    {translatableItems.map(item => {
                      const itemKey = getTranslationItemKey(item);
                      const itemStatus = getItemTranslationStatus(
                        item,
                        translationsByLanguage,
                        translatableLanguages,
                        getItemSourceText,
                        getTranslationItemKey,
                      );
                      return (
                        <button
                          key={itemKey}
                          type="button"
                          className={
                            selectedTranslation?.kind === "item" &&
                            selectedTranslation.key === itemKey
                              ? styles.translationListItemActive
                              : styles.translationListItem
                          }
                          onClick={() => setSelectedTranslation({ kind: "item", key: itemKey })}
                        >
                          <span className={styles.translationListTitle}>
                            {getItemSourceText(item)}
                          </span>
                          <span className={styles.translationListMeta}>
                            <span>{getItemKindLabel(item)}</span>
                            <span
                              className={
                                itemStatus === "translated"
                                  ? styles.statusTranslated
                                  : itemStatus === "outdated"
                                    ? styles.statusOutdated
                                    : styles.statusMissing
                              }
                            >
                              {itemStatus === "translated"
                                ? "Translated"
                                : itemStatus === "outdated"
                                  ? "Outdated"
                                  : "Missing"}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                    {hasFooterToTranslate && (
                      <>
                        <div
                          className={`${styles.translationPaneHeader} ${styles.translationSubsectionHeader}`}
                        >
                          Footer
                        </div>
                        <button
                          type="button"
                          className={
                            selectedTranslation?.kind === "footer"
                              ? styles.translationListItemActive
                              : styles.translationListItem
                          }
                          onClick={() => setSelectedTranslation({ kind: "footer" })}
                        >
                          <span className={styles.translationListTitle}>{footerSourceText}</span>
                          <span className={styles.translationListMeta}>
                            <span>Footer</span>
                            {(() => {
                              const footerStatus = getSectionTranslationStatus(
                                draftSurvey.definition,
                                FOOTER_SECTION,
                                footerTranslationsByLanguage,
                                translatableLanguages,
                              );
                              return (
                                <span
                                  className={
                                    footerStatus === "translated"
                                      ? styles.statusTranslated
                                      : footerStatus === "outdated"
                                        ? styles.statusOutdated
                                        : styles.statusMissing
                                  }
                                >
                                  {footerStatus === "translated"
                                    ? "Translated"
                                    : footerStatus === "outdated"
                                      ? "Outdated"
                                      : "Missing"}
                                </span>
                              );
                            })()}
                          </span>
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className={styles.translationEditorPane}>
                  <div className={styles.translationEditorScroll}>
                    {selectedTranslation?.kind === "item" && selectedTranslationItem ? (
                      <>
                        <div className={styles.translationEditorHeader}>
                          <h4>{getItemSourceText(selectedTranslationItem)}</h4>
                        </div>
                        {translatableLanguages.map(language => (
                          <div key={language} className={styles.translationBlock}>
                            <div className={styles.translationBlockHeader}>
                              <label
                                className={styles.translationLabel}
                                htmlFor={`translation-editor-${language}`}
                              >
                                Translation ({language.toUpperCase()}) {getLanguageFlag(language)}
                              </label>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() =>
                                  handleAutoTranslateItemForLanguage(
                                    selectedTranslationItem,
                                    language,
                                  )
                                }
                              >
                                Auto-translate {language.toUpperCase()}
                              </Button>
                            </div>
                            <textarea
                              id={`translation-editor-${language}`}
                              className={styles.translationTextarea}
                              value={translationEditorValues[language] ?? ""}
                              onChange={event => {
                                const { value } = event.target;
                                setTranslationEditorValues(prev => ({
                                  ...prev,
                                  [language]: value,
                                }));
                                writeTranslationEdit(
                                  language,
                                  getTranslationItemKey(selectedTranslationItem),
                                  value,
                                  getItemSourceText(selectedTranslationItem),
                                );
                              }}
                            />
                            {hasTranslatableQuestionDescription(selectedTranslationItem) && (
                              <div className={styles.inlineChoiceRow}>
                                <label
                                  className={styles.inlineChoiceLabel}
                                  htmlFor={`description-editor-${language}`}
                                >
                                  Description
                                </label>
                                <textarea
                                  id={`description-editor-${language}`}
                                  className={styles.translationTextarea}
                                  value={descriptionTranslationEditorValues[language] ?? ""}
                                  onChange={event => {
                                    const { value } = event.target;
                                    setDescriptionTranslationEditorValues(prev => ({
                                      ...prev,
                                      [language]: value,
                                    }));
                                    writeTranslationEdit(
                                      language,
                                      getDescriptionTranslationKey(selectedTranslationItem),
                                      value,
                                      getDescriptionSourceText(selectedTranslationItem.element),
                                    );
                                  }}
                                />
                              </div>
                            )}
                            {getTranslatableLabelFields(selectedTranslationItem).map(field => (
                              <div key={field.key} className={styles.inlineChoiceRow}>
                                <label
                                  className={styles.inlineChoiceLabel}
                                  htmlFor={`${field.editorIdPrefix}-${language}`}
                                >
                                  {field.displayName}
                                </label>
                                <textarea
                                  id={`${field.editorIdPrefix}-${language}`}
                                  className={styles.translationTextarea}
                                  value={
                                    labelFieldTranslationEditorValues[field.key]?.[language] ?? ""
                                  }
                                  onChange={event => {
                                    const { value } = event.target;
                                    setLabelFieldTranslationEditorValues(prev => ({
                                      ...prev,
                                      [field.key]: {
                                        ...(prev[field.key] ?? {}),
                                        [language]: value,
                                      },
                                    }));
                                    writeTranslationEdit(
                                      language,
                                      getLabelFieldTranslationKey(selectedTranslationItem, field),
                                      value,
                                      getLabelFieldSourceText(
                                        selectedTranslationItem.element,
                                        field,
                                      ),
                                    );
                                  }}
                                />
                              </div>
                            ))}
                            {hasTranslatableChoices(selectedTranslationItem.element) && (
                              <div className={styles.inlineChoices}>
                                {selectedTranslationItem.element.choices!.map(
                                  (choice, choiceIdx) => (
                                    <div
                                      key={getChoiceValue(choice)}
                                      className={styles.inlineChoiceRow}
                                    >
                                      <label
                                        className={styles.inlineChoiceLabel}
                                        htmlFor={`choice-editor-${language}-${choiceIdx}`}
                                      >
                                        {choiceIdx + 1}. {getChoiceSourceText(choice)}
                                      </label>
                                      <textarea
                                        id={`choice-editor-${language}-${choiceIdx}`}
                                        className={styles.translationTextarea}
                                        value={
                                          choiceTranslationEditorValues[language]?.[choiceIdx] ?? ""
                                        }
                                        onChange={event => {
                                          const { value } = event.target;
                                          setChoiceTranslationEditorValues(prev => ({
                                            ...prev,
                                            [language]: {
                                              ...(prev[language] ?? {}),
                                              [choiceIdx]: value,
                                            },
                                          }));
                                          writeTranslationEdit(
                                            language,
                                            getChoiceTranslationKey(
                                              selectedTranslationItem,
                                              choiceIdx,
                                            ),
                                            value,
                                            getChoiceSourceText(choice),
                                          );
                                        }}
                                      />
                                    </div>
                                  ),
                                )}
                              </div>
                            )}
                            {hasTranslatableRangeLabels(selectedTranslationItem.element) && (
                              <div className={styles.inlineChoices}>
                                {[...selectedTranslationItem.element.rangeLabels!]
                                  .sort((a, b) => a.order - b.order)
                                  .map(rl => (
                                    <div key={rl.id} className={styles.inlineChoiceRow}>
                                      <label
                                        className={styles.inlineChoiceLabel}
                                        htmlFor={`range-label-editor-${language}-${rl.id}`}
                                      >
                                        Scale label: {getRangeLabelSourceText(rl)}
                                      </label>
                                      <textarea
                                        id={`range-label-editor-${language}-${rl.id}`}
                                        className={styles.translationTextarea}
                                        value={
                                          rangeLabelTranslationEditorValues[language]?.[rl.id] ?? ""
                                        }
                                        onChange={event => {
                                          const { value } = event.target;
                                          setRangeLabelTranslationEditorValues(prev => ({
                                            ...prev,
                                            [language]: {
                                              ...(prev[language] ?? {}),
                                              [rl.id]: value,
                                            },
                                          }));
                                          writeTranslationEdit(
                                            language,
                                            getRangeLabelTranslationKey(
                                              selectedTranslationItem,
                                              rl.id,
                                            ),
                                            value,
                                            getRangeLabelSourceText(rl),
                                          );
                                        }}
                                      />
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </>
                    ) : selectedTranslation?.kind === "header" ? (
                      <>
                        <div className={styles.translationEditorHeader}>
                          <h4>{headerSourceText}</h4>
                        </div>
                        {translatableLanguages.map(language => (
                          <div key={language} className={styles.translationBlock}>
                            <div className={styles.translationBlockHeader}>
                              <label
                                className={styles.translationLabel}
                                htmlFor={`header-translation-editor-${language}`}
                              >
                                Translation ({language.toUpperCase()}) {getLanguageFlag(language)}
                              </label>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleAutoTranslateHeaderForLanguage(language)}
                              >
                                Auto-translate {language.toUpperCase()}
                              </Button>
                            </div>
                            <textarea
                              id={`header-translation-editor-${language}`}
                              className={styles.translationTextarea}
                              value={headerTranslationEditorValues[language] ?? ""}
                              onChange={event =>
                                setHeaderTranslationEditorValues(prev => ({
                                  ...prev,
                                  [language]: event.target.value,
                                }))
                              }
                            />
                          </div>
                        ))}
                      </>
                    ) : selectedTranslation?.kind === "footer" ? (
                      <>
                        <div className={styles.translationEditorHeader}>
                          <h4>{footerSourceText}</h4>
                        </div>
                        {translatableLanguages.map(language => (
                          <div key={language} className={styles.translationBlock}>
                            <div className={styles.translationBlockHeader}>
                              <label
                                className={styles.translationLabel}
                                htmlFor={`footer-translation-editor-${language}`}
                              >
                                Translation ({language.toUpperCase()}) {getLanguageFlag(language)}
                              </label>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleAutoTranslateFooterForLanguage(language)}
                              >
                                Auto-translate {language.toUpperCase()}
                              </Button>
                            </div>
                            <textarea
                              id={`footer-translation-editor-${language}`}
                              className={styles.translationTextarea}
                              value={footerTranslationEditorValues[language] ?? ""}
                              onChange={event =>
                                setFooterTranslationEditorValues(prev => ({
                                  ...prev,
                                  [language]: event.target.value,
                                }))
                              }
                            />
                          </div>
                        ))}
                      </>
                    ) : (
                      <EmptyState
                        variant="info"
                        title="Select an item"
                        message="Pick a survey item from the list to edit its translation."
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {activeTab === "preview" && (
          <SurveyPreviewTab
            definition={previewDefinition}
            survey={draftSurvey}
            taxonomyOptions={taxonomyRuleOptions}
            previewContext={previewContext}
            onPreviewContextChange={setPreviewContext}
            collapsed={previewCollapsed}
            onCollapsedChange={setPreviewCollapsed}
          />
        )}
        {activeTab === "settings" && (
          <div className={styles.section}>
            <div className={styles.settingsForm}>
              <div className={styles.settingsSection}>
                <h4 className={styles.settingsSectionTitle}>Editable</h4>
                <div className={styles.settingsGrid}>
                  <Input
                    label="Survey Name"
                    value={draftSurvey.name}
                    onChange={event => {
                      setDraftSurvey(prev =>
                        prev
                          ? {
                              ...prev,
                              name: event.target.value,
                            }
                          : prev,
                      );
                      clearSettingError("name");
                    }}
                    error={settingsErrors.name}
                    required
                    fullWidth
                  />
                  <Select
                    label="Default Language"
                    options={supportedLanguageOptions}
                    value={draftSurvey.defaultLanguage}
                    onChange={event =>
                      setDraftSurvey(prev =>
                        prev
                          ? {
                              ...prev,
                              defaultLanguage: event.target.value,
                            }
                          : prev,
                      )
                    }
                    fullWidth
                  />
                </div>
              </div>
              <div className={styles.settingsSection}>
                <h4 className={styles.settingsSectionTitle}>Survey Metadata</h4>
                <div className={styles.settingsGrid}>
                  {newAxisSurvey ? (
                    newAxisEditable ? (
                      <Select
                        label="Edition"
                        options={newAxisEditionSelectOptions}
                        value={draftSurvey.editionId ?? ""}
                        onChange={event => {
                          const next = newAxisEditions?.find(
                            edition => edition.editionId === event.target.value,
                          );
                          if (!next) return;
                          setDraftSurvey(prev =>
                            prev
                              ? {
                                  ...prev,
                                  editionId: next.editionId,
                                  publicationId: next.publicationId,
                                  publication: next.publication,
                                  edition: next.editionName,
                                }
                              : prev,
                          );
                          clearSettingError("edition");
                        }}
                        error={
                          newAxisEditionsError
                            ? "Failed to load editions. Please try again."
                            : settingsErrors.edition
                        }
                        fullWidth
                        required
                        disabled={
                          !newAxisEditions || isPublishedSurvey || taxonomyLocksEditionPublication
                        }
                        helperText={
                          isPublishedSurvey
                            ? "Edition is locked after publication."
                            : taxonomyLocksEditionPublication
                              ? EDITION_LOCKED_BY_TAXONOMY_RULES_MESSAGE
                              : undefined
                        }
                      />
                    ) : (
                      <div className={styles.settingsInfoField}>
                        <span className={styles.settingsInfoLabel}>Edition</span>
                        <div className={styles.settingsStatusValue}>
                          {storedNewAxisEditionLabel}
                        </div>
                        <p className={styles.settingsInfoHint}>
                          This survey uses the new taxonomy. Its edition can only be changed while
                          the new taxonomy is enabled.
                        </p>
                      </div>
                    )
                  ) : (
                    <>
                      <Select
                        label="Edition"
                        options={Array.from(
                          { length: new Date().getFullYear() + 1 - 2017 },
                          (_, idx) => {
                            const year = String(new Date().getFullYear() + 1 - idx);
                            return { value: year, label: year };
                          },
                        )}
                        value={selectedEditionYear}
                        onChange={event => {
                          const nextYear = event.target.value;
                          // Switching the year only updates the filter; the full edition
                          // string and the publication are cleared until a publication is
                          // re-picked, mirroring SurveyFormModal's reset-on-edition-change.
                          setSelectedEditionYear(nextYear);
                          setDraftSurvey(prev =>
                            prev
                              ? {
                                  ...prev,
                                  edition: "",
                                  publicationId: 0,
                                  publication: "",
                                }
                              : prev,
                          );
                          clearSettingError("edition");
                          clearSettingError("publication");
                        }}
                        error={settingsErrors.edition}
                        fullWidth
                        required
                        disabled={isPublishedSurvey || taxonomyLocksEditionPublication}
                        helperText={
                          isPublishedSurvey
                            ? "Edition is locked after publication."
                            : taxonomyLocksEditionPublication
                              ? EDITION_LOCKED_BY_TAXONOMY_RULES_MESSAGE
                              : undefined
                        }
                      />
                      <Select
                        label="Publication"
                        options={publicationOptions}
                        value={selectedPublicationId}
                        onChange={event => {
                          const nextId = Number(event.target.value);
                          const selectedPublication =
                            publicationsData?.publications.find(
                              (publication: LegacyPublication) => publication.id === nextId,
                            ) ?? null;
                          setDraftSurvey(prev =>
                            prev
                              ? {
                                  ...prev,
                                  publicationId: nextId,
                                  publication: selectedPublication?.publication ?? "",
                                  // Persist the full edition string ("2026 Edition ...")
                                  // from the chosen publication, not the bare year.
                                  edition: selectedPublication?.edition ?? "",
                                }
                              : prev,
                          );
                          clearSettingError("edition");
                          clearSettingError("publication");
                        }}
                        error={settingsErrors.publication}
                        disabled={
                          !selectedEditionYear ||
                          isPublishedSurvey ||
                          taxonomyLocksEditionPublication
                        }
                        fullWidth
                        required
                        helperText={
                          isPublishedSurvey
                            ? "Publication is locked after publication."
                            : taxonomyLocksEditionPublication
                              ? PUBLICATION_LOCKED_BY_TAXONOMY_RULES_MESSAGE
                              : undefined
                        }
                      />
                    </>
                  )}
                  <div className={styles.settingsInfoField}>
                    <span className={styles.settingsInfoLabel}>Status</span>
                    <div className={styles.settingsStatusValue}>
                      <Chip
                        size="small"
                        color={STATUS_CHIP_COLOR[draftSurvey.status]}
                        className={
                          draftSurvey.status === "draft" ? styles.draftStatusChip : undefined
                        }
                      >
                        {draftSurvey.status === "published" ? "Published" : "Draft"}
                      </Chip>
                    </div>
                    <p className={styles.settingsInfoHint}>
                      {draftSurvey.status === "published"
                        ? "Published surveys cannot be reverted to draft."
                        : "Status changes to Published after successful publish validation."}
                    </p>
                  </div>
                </div>
                <ExceptionConditionsFields
                  values={toSurveyConditionValues(draftSurvey)}
                  onChange={next =>
                    setDraftSurvey(prev =>
                      prev
                        ? {
                            ...prev,
                            regionTypes: next.regionTypes,
                            refereeTypes: next.refereeTypes,
                            refereeCategories: next.refereeCategories,
                          }
                        : prev,
                    )
                  }
                  expanded={conditionsExpanded}
                  onToggleExpanded={() => setConditionsExpanded(prev => !prev)}
                />
                {duplicateSurvey && <DuplicateSurveyWarning survey={duplicateSurvey} />}
              </div>
            </div>
          </div>
        )}
      </div>

      <QuestionFormModal
        isOpen={questionModal.open}
        onClose={closeQuestionModal}
        question={editingQuestion}
        onSave={handleQuestionSubmit}
        taxonomyOptions={taxonomyRuleOptions}
        createModeSaveLabel="Add question"
      />

      <Modal
        isOpen={contentModal.open}
        onClose={closeContentModal}
        title={
          contentModal.mode === "add"
            ? contentModal.kind === "header"
              ? "Add Header"
              : "Add Free Text"
            : contentModal.kind === "header"
              ? "Edit Header"
              : "Edit Free Text"
        }
        size={contentModal.kind === "header" ? "mediumLarge" : "small"}
        backdropClick={false}
      >
        <div className={styles.modalContent}>
          <label className={styles.translationLabel}>
            {contentModal.kind === "header" ? "Header Text" : "Free Text Content"}
          </label>
          <textarea
            className={styles.contentTextarea}
            value={contentForm.text}
            onChange={event => setContentForm(prev => ({ ...prev, text: event.target.value }))}
            required
            rows={contentModal.kind === "header" ? 3 : 6}
          />
          <p className={styles.inlineHint}>
            {contentModal.kind === "header"
              ? "Use short section labels to break up long surveys."
              : "Add a concise paragraph shown to respondents."}
          </p>
          {contentModal.kind === "header" && (
            <>
              <Select
                label="Header Level"
                options={HEADER_TAG_OPTIONS}
                value={contentForm.headerTag}
                onChange={event =>
                  setContentForm(prev => ({
                    ...prev,
                    headerTag: isHeaderTag(event.target.value)
                      ? event.target.value
                      : DEFAULT_HEADER_TAG,
                  }))
                }
                fullWidth
              />
              <FilterRulesEditor
                ref={contentDisplayRulesEditorRef}
                rules={contentDisplayRules}
                onChange={setContentDisplayRules}
                taxonomyOptions={taxonomyRuleOptions}
              />
            </>
          )}
          <div className={styles.modalActions}>
            <Button variant="outlined" size="small" onClick={closeContentModal}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" size="small" onClick={handleContentSubmit}>
              {contentModal.mode === "add"
                ? contentModal.kind === "header"
                  ? "Add Header"
                  : "Add Free Text"
                : "Save"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={libraryModalOpen}
        onClose={() => {
          setLibraryModalOpen(false);
          setLibrarySearch("");
        }}
        title="Pick from Library"
        size="mediumLarge"
        backdropClick={false}
      >
        <div className={styles.libraryModalContent}>
          {libraryLoading && libraryResponse === null ? (
            <LoadingState message="Loading question library..." />
          ) : libraryError ? (
            <ErrorBanner error={libraryError.message} onRetry={refetchLibrary} loading={false} />
          ) : (libraryResponse?.questions.length ?? 0) === 0 ? (
            <EmptyState
              variant="empty"
              title="No library questions available"
              message="Create questions in the question library and return here to insert them."
            />
          ) : (
            <>
              <SearchBar
                value={librarySearch}
                onChange={setLibrarySearch}
                onClear={() => setLibrarySearch("")}
                placeholder="Search questions..."
              />
              {filteredLibraryQuestions.length === 0 ? (
                <EmptyState
                  variant="empty"
                  title="No matching questions"
                  message="No library questions match your search. Try a different term."
                />
              ) : (
                <div className={styles.libraryList}>
                  {filteredLibraryQuestions.map(question => (
                    <button
                      key={question.id}
                      type="button"
                      className={styles.libraryItem}
                      onClick={() => handleAddFromLibrary(question)}
                      onKeyDown={event => {
                        if (event.key !== "Enter" && event.key !== " ") {
                          return;
                        }
                        event.preventDefault();
                        handleAddFromLibrary(question);
                      }}
                      aria-label={`Add question from library: ${getLocalizedTextDefault(
                        question.text,
                      )}`}
                    >
                      <div className={styles.libraryInfo}>
                        <div className={styles.libraryTitleRow}>
                          <div className={styles.libraryTitleText}>
                            <span className={styles.questionTitle}>
                              {getLocalizedTextDefault(question.text)}
                            </span>
                            {question.internalLabel && (
                              <span className={styles.libraryInternalLabel}>
                                {question.internalLabel}
                              </span>
                            )}
                          </div>
                          <div className={styles.questionMeta}>
                            <Chip size="small" variant="outlined" color="primary">
                              {question.type}
                            </Chip>
                            {question.required && (
                              <Chip size="small" variant="outlined" color="error">
                                Required
                              </Chip>
                            )}
                          </div>
                        </div>
                        <div className={styles.libraryRulesRow}>
                          {renderLibraryRules(question.displayRules, ruleValueLabelMaps)}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      {publishConfirmOpen && (
        <ConfirmationModal
          size="small"
          title="Publish Survey"
          message="Are you sure you want to publish this survey?"
          warningMessage="This action cannot be reverted."
          backdropClick={false}
          confirmButtonText="Publish"
          onClose={() => setPublishConfirmOpen(false)}
          onConfirm={handlePublishConfirm}
        />
      )}

      {testimonialWarningOpen && (
        <ConfirmationModal
          size="small"
          title="Publish without testimonial questions?"
          message="If you plan to use this survey as research data, are you sure you don't want any Testimonial or Multi Testimonial questions? Without these questions, no testimonials will be generated from this survey."
          warningMessage="This action cannot be reverted."
          backdropClick={false}
          confirmButtonText="Publish"
          cancelButtonText="Cancel"
          onClose={() => setTestimonialWarningOpen(false)}
          onConfirm={doPublish}
        />
      )}

      <MissingTranslationsModal
        isOpen={!!serverMissingTranslations}
        onClose={() => setServerMissingTranslations(null)}
        onGoToTranslations={() => {
          setActiveTab("translations");
          setServerMissingTranslations(null);
        }}
        missing={serverMissingTranslations ?? []}
        definition={draftSurvey.definition}
        languageLabels={supportedLanguageOptions.reduce<Record<string, string>>((acc, option) => {
          acc[option.value] = option.label;
          return acc;
        }, {})}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.type === "error" ? 5000 : 3000}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default SurveyDetailsPage;
